export interface Organization {
  id: string;
  name: string;
  role: string;
  createdAt: string;
  /**
   * UI-18 — what sort of organization this is, in its owner's own words ("IT
   * consultancy"). Null until somebody fills it in, never an empty string.
   */
  kind: string | null;
  /** UI-18 — a couple of sentences on what it does. Read by every prompt that has to
   * judge what matters in this organization's material. */
  description: string | null;
  /**
   * UI-18 (0035) — the other names this organization is called by: "CM", "CM.com".
   *
   * Not read by anything that reasons — what "we" means is `description`'s job. These are
   * how the words *sound*, handed to the transcriber so it listens for them, which is why
   * an organization's own name being mistranscribed stops being a thing anybody has to fix
   * by hand. Empty rather than null when nobody has added one.
   */
  aliases: string[];
}

/** UI-18 — the fields the Account settings form writes, all at once. */
export interface OrganizationProfileEdit {
  name: string;
  kind: string | null;
  description: string | null;
  aliases: string[];
}

export interface UserSettings {
  developerMode: boolean;
  uiMode: "full" | "simple";
  theme: "dark" | "light";
  defaultLanguage: string | null;
  defaultSearchScope: "world" | "insights" | "both";
}

export interface Me {
  user: {
    id: string;
    email: string | null;
    name: string | null;
    username: string | null;
    avatarUrl: string | null;
  };
  organization: Organization | null;
  organizations: Organization[];
  settings: UserSettings | null;
}

/** One ingested source — a recording, a meeting, or a document — and the file it arrived as. */
export interface SourceMaterial {
  id: string;
  kind: "recording" | "chat_log" | "document" | "note" | "meeting";
  title: string | null;
  /** ISO date, no time: the date of the meeting or document, not of the upload. */
  sourceDate: string | null;
  language: string | null;
  createdAt: string;
  originalFilename: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  /** The recording a meeting was split from (recording/meeting split) — null for every
   * other kind. */
  parentSourceMaterialId: string | null;
  /** The optional blurb a human added when naming a meeting — null for every other kind. */
  description: string | null;
  /** The `summarize` step's paragraph — null until it has run, and always null for a kind
   * that has no summarize step of its own. */
  summary: string | null;
  summaryGeneratedAt: string | null;
  /** CAP-8 — the uploader marked this note or document as their own words, at capture. What is
   * approved out of it lands in the world view as theirs, pinned. Absent on older answers. */
  authoredByOwner?: boolean;
  /** The capture device's own name (Settings), for a recording a Recorder PC filed. Null
   * for a manual upload — show `originalFilename` instead. */
  recorderName: string | null;
  /** ISO instant: precisely when the Recorder started this recording. Null for anything
   * filed before the Server carried it, or for a manual upload, which has no such moment
   * to report. For a meeting, this is the parent recording's own value — a meeting's
   * segments keep the recording's original millisecond offsets. */
  startedAt: string | null;
}

/**
 * One meeting split off a recording (recording/meeting split): the content-phase unit
 * — `correct_transcript`, `summarize`, and the three INSIGHTS steps — run over it rather
 * than over the raw recording, so its own timeline, name and description are what
 * extraction reads by.
 */
export interface Meeting {
  id: string;
  /** The recording it was split from — every meeting from one recording shares this,
   * however many times it has been split further. */
  parentSourceMaterialId: string;
  title: string | null;
  description: string | null;
  sourceDate: string | null;
  createdAt: string;
  durationMs: number | null;
  /** The `summarize` step's paragraph, or null until it has run. */
  summary: string | null;
  summaryGeneratedAt: string | null;
  /** The newest content-phase run's status, or null for a meeting whose run has not
   * been started yet. */
  status: "queued" | "processing" | "done" | "failed" | "cancelled" | null;
  /** SPCH-13 — adopted from a proposal as lunch, a walk or the recorder in a pocket: kept in
   * the timeline, but not offered to the content phase. Absent from a Server older than it. */
  notAMeeting?: boolean;
}

/**
 * SPCH-13 — one stretch of a long recording, as the audio phase proposes it. People appear
 * only as diarizer labels: in `voices`, in `border.joined`/`border.left`, and as `[S<label>]`
 * inside `title` and `description`, so the page names them from the recording's speakers as
 * they stand right now.
 */
export interface ProposedStretch {
  startMs: number;
  endMs: number;
  kind: "meeting" | "other";
  title: string | null;
  description: string | null;
  talkSecondsPerMinute: number;
  floorDb: number | null;
  rangeDb: number | null;
  voices: { speaker: string; speechMs: number }[];
  /** Why this stretch starts here rather than continuing the one before it; null for the first. */
  border: {
    talkChanged: boolean;
    roomChangeDb: number | null;
    voiceChange: number;
    joined: string[];
    left: string[];
    /** What was said that put the border here: a meeting closing just before it, one opening at
     * it. Absent for a border the room and voices alone placed, and from an older Server. */
    cues?: { kind: "opens" | "closes"; quote: string; atMs: number }[] | null;
  } | null;
}

/** One group of an adopted proposal: where it starts — a proposal stretch, or a transcript line
 * a part was cut or moved to by hand (SPCH-14) — and what becomes of it. */
export type AdoptedProposalGroup = ({ startIndex: number } | { fromSequence: number }) & {
  title: string | null;
  description: string | null;
  kind: "meeting" | "discard";
};

export interface MeetingProposal {
  /** `unavailable` for a recording processed before SPCH-13 existed. */
  status: "unavailable" | "pending" | "failed" | "ready";
  /** False once any of the recording's meetings has been started. */
  canApply: boolean;
  /** True when meetings were started but none is mid-run: adopting again replaces them, and
   *  discards what was read off them. Absent on an older Server. */
  canReplace?: boolean;
  /** True while the recording holds only its whole-recording default meeting and nobody has
   *  adopted this proposal — the Server then refuses to process that default meeting. Absent
   *  on an older Server. */
  awaitingAdoption?: boolean;
  stretches: ProposedStretch[];
  /** The Reasoner model that named the stretches, or null when they are untitled. */
  model: string | null;
}

/**
 * UI-4 — one row of the recordings list: what it is, and where the pipeline has got
 * it to.
 */
export interface Recording {
  id: string;
  title: string | null;
  originalFilename: string | null;
  /** ISO date, no time: the date of the meeting, not of the upload. */
  sourceDate: string | null;
  createdAt: string;
  language: string | null;
  /** Null until `transcribe` has listened to it. */
  durationMs: number | null;
  /** Null until `reconcile` has grouped it. Zero means the diarizer heard nobody. */
  speakerCount: number | null;
  /** Null for a recording that has no run at all — uploaded before there was one. */
  status: "queued" | "processing" | "done" | "failed" | "cancelled" | null;
  /** ISO instant: precisely when the Recorder started this recording. Null for anything
   * filed before the Server carried it, or for a manual upload. */
  startedAt: string | null;
}

/** CAP-3 — what the Server says about a file it has seen before. */
export interface DuplicateSource {
  sourceMaterialId: string;
  title: string | null;
  originalFilename: string | null;
  uploadedAt: string;
  /** What the file was called this time, which may differ from the original. */
  uploadedFilename: string;
}

export interface NewUpload {
  kind: "recording" | "document";
  file: File;
  title: string;
  /** yyyy-MM-dd, or empty for "not known" — which is a real answer, not a gap. */
  sourceDate: string;
  /** Two-letter ISO 639-1 code, or "auto". */
  language: string;
  /** How many people are on the recording, or "auto" to let the diarizer decide
   * (SPCH-3). Only meaningful for audio. */
  expectedSpeakers: string;
  /** CAP-8 — the document is the uploader's own words. Only a document may say so. */
  authoredByMe?: boolean;
}

/** ARCH-2 — one step of a run, as the interface reads it. */
export interface PipelineStep {
  id: string;
  stepName: string;
  sequence: number;
  status: "pending" | "claimed" | "running" | "done" | "failed" | "cancelled";
  claimedBy: string | null;
  /** What that machine calls itself, so the detail view can name it (UI-5). */
  claimedByName: string | null;
  leaseExpiresAt: string | null;
  epoch: number;
  attempts: number;
  error: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  /**
   * What this step ran on, or will (ARCH-8). Read back from the step's own input
   * artifact once claimed — what it actually ran with — and resolved live from
   * Settings → Engines &amp; models while it is still pending. Null for a step with no
   * engine at all (`reconcile`, `name_speakers` run no model).
   */
  model: string | null;
  /**
   * What this step's one Anthropic call cost, in USD (ARCH-8), computed and stored when
   * the step completed. Null for a step that made no Anthropic call — every local/GPU
   * step, and a step that has not completed yet — never a stored zero.
   */
  costUsd: number | null;
  /**
   * Why the last attempt ended without a result, as one stable word (0068): `worker_lost`,
   * `deadline_exceeded`, `cancelled`, `sidecar_unavailable`, `gpu_error`, `out_of_memory`,
   * `model_unavailable`, `model_answer_unusable`, `invalid_input`, `configuration`, `internal`.
   * Null while nothing has gone wrong. Absent from a Server older than the field.
   */
  failureCode?: string | null;
  /** Every attempt at this step, oldest first (0068). Absent from an older Server. */
  history?: StepEvent[];
}

/** One change in a step's life (0068). `error` and `failureCode` say why an attempt ended. */
export interface StepEvent {
  occurredAt: string;
  event: "claimed" | "running" | "done" | "retried" | "requeued" | "failed" | "cancelled" | "restarted";
  attempt: number;
  workerId: string | null;
  workerName: string | null;
  error: string | null;
  failureCode: string | null;
}

/** One pass of the step chain over one source material. */
export interface PipelineRun {
  id: string;
  sourceMaterialId: string;
  status: "queued" | "processing" | "done" | "failed" | "cancelled";
  /** The step the last restart re-entered the chain at (SPCH-10). */
  restartedFromStep: string | null;
  /**
   * SPCH-9 — cancelled, and one step has not noticed yet.
   *
   * Derived by the Server rather than stored, because it is true only for as long as
   * one claimant takes to finish what it was already doing. Since 0068 the running step
   * learns at its next heartbeat, so this lasts seconds rather than a whole step.
   */
  cancelling: boolean;
  createdAt: string;
  completedAt: string | null;
  steps: PipelineStep[];
}

/**
 * ARCH-8 — one stored artifact, as the Debug tab lists it.
 *
 * `content` is the body whether the artifact sits in the row or on the volume — a
 * volume-backed one is read back for the tab, capped Server-side, with `truncated`
 * saying the file goes on past what arrived. It is null only when the row points at a
 * file the volume no longer has, and then `storagePath` is all there is to show.
 */
export interface StepArtifact {
  id: string;
  direction: "input" | "output";
  createdAt: string;
  content: string | null;
  storagePath: string | null;
  sizeBytes: number | null;
  truncated: boolean;
}

/**
 * UI-13 — one row of the setup checklist.
 *
 * `later` is a third state and not a stalled `needs-you`: capture and calendar are M10
 * and M11, so that row is drawn and named rather than sitting permanently unticked. A
 * checklist that can never reach the end is one nobody looks at twice.
 */
export interface SetupStep {
  id: string;
  label: string;
  detail: string;
  /**
   * `later` is a step whose feature does not exist yet and names the milestone that
   * brings it; `optional` is one that exists and that nothing is waiting on (M11.2).
   * Both are uncounted, and only `optional` leads somewhere — there is something to do
   * there, which is the whole difference.
   */
  state: "done" | "needs-you" | "later" | "optional";
  /** The settings section that finishes this step. */
  section: string | null;
  milestone: string | null;
}

export interface SetupState {
  steps: SetupStep[];
  done: number;
  total: number;
  /** What the rail's warning dot reads. */
  blocking: number;
}

/**
 * UI-5 — one participant slot in a recording.
 *
 * `sampleSegmentId` and its span are this speaker's longest turn, which is what the
 * Speakers tab's play button plays. Null for a slot nothing was ever attributed to.
 */
export interface TranscriptSpeaker {
  id: string;
  /** The diarizer's cluster label, until somebody confirms who it is (SPCH-6). */
  label: string | null;
  confirmedName: string | null;
  status: "unconfirmed" | "confirmed";
  segmentCount: number;
  speakingMs: number;
  sampleSegmentId: string | null;
  sampleStartMs: number | null;
  sampleEndMs: number | null;
  /** The person a human confirmed this voice to be (SPCH-6), or null while unconfirmed. */
  personId: string | null;
  /** Who the gallery proposed (SPCH-5) — a suggestion, kept apart from {@link personId}. */
  matchedPersonId: string | null;
  matchedPersonName: string | null;
  /** The similarity behind the proposal; null when nothing matched ("Unmatched"). */
  matchScore: number | null;
  /**
   * How many voiceprints this voice currently has — what confirming it would teach the
   * gallery (SPCH-5).
   *
   * Zero before `voiceprint` has run, and zero again once a reassignment invalidated
   * them (0037): the fingerprints are cut from turns, so moving a turn makes both voices'
   * samples claims about a cluster that no longer exists. The tab says so, because a
   * confirmation that teaches the gallery nothing looks exactly like one that does.
   */
  sampleCount: number;
  /**
   * The voice in this same recording that sounds most like this one, when there is one
   * close enough to be worth offering (SPCH-3).
   *
   * The diarizer now splits on purpose where it is unsure, because two rows are one click
   * to join and one row holding two people is not. This is that click: the tab offers
   * "sounds like Speaker 3", and taking it confirms both to the same person.
   */
  soundsLikeId: string | null;
  soundsLikeScore: number | null;
  /**
   * This voice does not sound like one person — its own samples disagree, so it is a
   * cluster holding two speakers that the split check did not catch.
   *
   * Nothing from it went into the gallery, deliberately: filing two people under one name
   * would follow every recording after this one (0038). The row says so rather than
   * offering a name it cannot stand behind.
   */
  mixed: boolean;
}

/** One diarized turn — the unit the Transcript tab draws a line for. */
export interface TranscriptSegment {
  id: string;
  speakerId: string | null;
  sequence: number;
  text: string;
  startsAtMs: number | null;
  endsAtMs: number | null;
  /**
   * Whether this segment carries a correction the Reasoner proposed and the owner has
   * not yet decided (SPCH-8). The tab draws an `Unverified` pill and links to Transcript
   * Review; the effective text stays the pipeline's until the change is approved.
   */
  unverified: boolean;
}

/**
 * A recording's transcript, as the detail view reads it.
 *
 * Turns, not words. Word-level timings drive Transcript Review (UI-12, M3.2), which is
 * its own screen and asks for its own payload — an hour of speech is ten thousand
 * words and this tab draws none of them individually.
 */
export interface Transcript {
  speakers: TranscriptSpeaker[];
  segments: TranscriptSegment[];
  /** Null until the owner has read it through in Transcript Review and said it is right. */
  reviewedAt: string | null;
  /** How many segments are flagged for a second look (UI-12). */
  flaggedCount: number;
}

/**
 * UI-12 — one word, as the reading view draws it.
 *
 * Confidence has **three** states and the screen must draw three: a number, a low
 * number, and null. Null means nothing can vouch for this word — the aligner could not
 * match it back, or a person typed it — and drawing that as "low" would tell the owner
 * to listen closely to hundreds of words that are probably fine.
 */
export interface ReviewWord {
  word: string;
  startMs: number;
  endMs: number;
  confidence: number | null;
}

/** The standing human correction on a segment, and what it replaced. */
export interface ReviewCorrection {
  id: string;
  originalText: string;
  correctedText: string;
  createdAt: string;
}

/**
 * One correction the Reasoner proposed for a segment (SPCH-8), as the review screen
 * draws it: the before and after, why, and whether it has been decided yet.
 *
 * A `pending` one is the `Unverified` change the owner approves or discards; an `applied`
 * one is already the segment's effective text; a `discarded` one is kept as provenance,
 * shown struck through so a restart does not silently bring it back.
 */
export interface ReviewProposal {
  id: string;
  originalText: string;
  correctedText: string;
  category: string | null;
  reasoning: string | null;
  status: "pending" | "applied" | "discarded";
  createdAt: string;
}

/**
 * One segment as Transcript Review reads it.
 *
 * `text` is the *effective* text — the owner's own correction's when there is one, then
 * an approved Reasoner correction's, then the pipeline's — so nothing in the interface
 * has to ask which it is looking at. `correction` is the owner's own standing edit (for
 * Save/Revert); `proposals` are the Reasoner's suggestions (for approve/discard).
 */
export interface ReviewSegment {
  id: string;
  speakerId: string | null;
  sequence: number;
  text: string;
  startsAtMs: number | null;
  endsAtMs: number | null;
  flaggedAt: string | null;
  correction: ReviewCorrection | null;
  proposals: ReviewProposal[];
  words: ReviewWord[];
}

export interface ReviewRecording {
  id: string;
  title: string | null;
  originalFilename: string | null;
  sourceDate: string | null;
  createdAt: string;
  language: string | null;
  durationMs: number | null;
  reviewedAt: string | null;
  /** ISO instant: precisely when the recording started, or its parent's when this is a
   * meeting. Null for anything filed before the Server carried it. */
  startedAt: string | null;
}

/**
 * Everything Transcript Review needs, in one payload — every word included.
 *
 * The opposite trade to `Transcript`, and deliberately: that one draws turns and would
 * ship ten thousand words to render none of them, while this screen *is* the word-level
 * view. Asking per segment instead would make scrubbing to minute fifty a network
 * operation.
 */
export interface TranscriptReview {
  recording: ReviewRecording;
  speakers: TranscriptSpeaker[];
  segments: ReviewSegment[];
}

/** SEC-4 — a short-lived address a bare `<audio>` element can actually use. */
/** UI-14 — one setting on an engine, as the Server says it should be drawn. */
export interface EngineParameter {
  key: string;
  label: string;
  kind: "select" | "switch" | "number";
  options: string[] | null;
  hint: string | null;
  default: string | null;
  minimum: number | null;
  maximum: number | null;
  /**
   * ARCH-12 — whether this may also be set for one step on its own.
   *
   * False for anything that changes what is *loaded* rather than what is asked of it:
   * the card holds one model at a time, so a per-step context cap would unload and
   * reload twelve gigabytes between two steps of one recording.
   */
  perStep: boolean;
  /** The steps that read this, or null for every step. A per-step row draws only these. */
  steps: string[] | null;
}

export interface EngineModel {
  id: string;
  label: string;
  /** Weight size on disk. Null for a cloud model, which has none. */
  size: string | null;
  note: string | null;
}

/** One place an engine can run, and everything true about running it there. */
export interface EnginePlacement {
  placement: "worker" | "server" | "cloud";
  /** "Local", "Worker GPU", "Server CPU", "Cloud" — the handoff's word per engine. */
  label: string;
  where: string;
  hint: string;
  /** False means nothing implements this yet; `milestone` says when it arrives. */
  available: boolean;
  milestone: string | null;
  models: EngineModel[];
  parameters: EngineParameter[];
  provider: string | null;
}

/** ARCH-12 — what one step wants from its engine when nobody has said otherwise. */
export interface StepProfile {
  step: string;
  /** One sentence on why this step wants what it wants, drawn beside its row. */
  why: string;
  parameters: Record<string, string | number | boolean>;
}

export interface EngineDefinition {
  engine: string;
  what: string;
  steps: string[];
  placements: EnginePlacement[];
  stepProfiles: StepProfile[];
  /**
   * Whether this engine has anything worth drawing a per-step row for: more than one
   * step, and at least one parameter that can be scoped to one of them. Asked rather
   * than assumed, so an engine that grows both starts showing rows with no edit here.
   */
  scopesBySteps: boolean;
}

export interface EnginePreset {
  id: string;
  label: string;
  note: string;
  placements: Record<string, string>;
}

/**
 * One step's parameters as the step will actually receive them: the catalogue's profile
 * for it, then the engine's own values, then anything set for this step alone (ARCH-12).
 *
 * No placement and no model, because neither is per step.
 */
export interface ResolvedStep {
  step: string;
  why: string | null;
  parameters: Record<string, string | number | boolean>;
  /** True while this step has a row of its own rather than running on what it inherited. */
  explicit: boolean;
}

/** What an engine is actually set to, catalogue defaults already folded in. */
export interface ResolvedEngine {
  engine: string;
  placement: string;
  model: string;
  parameters: Record<string, string | number | boolean>;
  /** False while the engine is running on the default nobody has chosen yet. */
  explicit: boolean;
  /** Each step this engine owns, resolved. Empty where the steps have nothing to disagree about. */
  steps: ResolvedStep[];
}

/** ARCH-6 — one worker and what it says it has on disk. */
export interface WorkerInventory {
  id: string;
  name: string | null;
  lastHeartbeatAt: string | null;
  models: Record<string, "present" | "fetching" | "missing" | "failed">;
  /**
   * How much context its local Reasoner had when it last answered — prompt and answer
   * together, as the sidecar measured it. Null until it has reasoned locally once. It moves
   * with the machine's VRAM budget, so it is reported rather than known in advance.
   */
  reasonerRoom: { model: string; contextTokens: number } | null;
}

export interface EngineSettings {
  catalogue: EngineDefinition[];
  presets: EnginePreset[];
  engines: ResolvedEngine[];
  workers: WorkerInventory[];
}

export interface ProviderDefinition {
  provider: string;
  name: string;
  what: string;
  keyLabel: string;
  keyPlaceholder: string;
  docs: string;
}

/**
 * A provider as the browser is allowed to see it. There is deliberately no field that
 * could hold a key: the Server never sends one back.
 */
export interface ProviderCredential {
  provider: string;
  connected: boolean;
  /** The last four characters, so the row can read as ••••••••1234. */
  hint: string | null;
  lastTestedAt: string | null;
  lastTestLatencyMs: number | null;
  lastTestError: string | null;
  settings: Record<string, unknown>;
}

/** UI-22 — one provider/model pair's spend for the month. */
export interface CostsByModel {
  provider: string;
  model: string;
  calls: number;
  costUsd: number | null;
  unpricedCalls: number;
}

/** One pipeline step's — or chat's — spend for the month. */
export interface CostsByOperation {
  operation: string;
  calls: number;
  costUsd: number | null;
  unpricedCalls: number;
}

/** One content-phase meeting under a recording's roll-up. */
export interface CostsMeeting {
  meetingId: string | null;
  title: string | null;
  deleted: boolean;
  costUsd: number | null;
}

/** One recording's spend for the month, its meetings broken out. */
export interface CostsByRecording {
  recordingId: string | null;
  title: string | null;
  deleted: boolean;
  costUsd: number | null;
  meetings: CostsMeeting[];
}

/** One synthesis pass, across every attempt. */
export interface CostsSynthesisBatch {
  batchId: string;
  createdAt: string;
  insightCount: number;
  costUsd: number | null;
}

/** Spend not about a recording and not a synthesis pass. */
export interface CostsOther {
  kind: string;
  calls: number;
  costUsd: number | null;
  unpricedCalls: number;
}

export interface CostsMonth {
  month: string;
  costUsd: number | null;
}

/** UI-22 — everything the Costs section draws, from one read over `cloud_usage`. */
export interface CostsActivity {
  totalCostUsd: number | null;
  unpricedCalls: number;
  byProviderAndModel: CostsByModel[];
  byOperation: CostsByOperation[];
  byRecording: CostsByRecording[];
  synthesisBatches: CostsSynthesisBatch[];
  other: CostsOther[];
  trailingMonths: CostsMonth[];
}

export interface ProviderSettings {
  catalogue: ProviderDefinition[];
  providers: ProviderCredential[];
  /** Engines placed on this provider right now. */
  usedBy: Record<string, string[]>;
  /** Engines that could be placed on it. */
  serves: Record<string, string[]>;
  /** Whether a key can be stored at all — the Server needs an encryption key first. */
  keyStorage: { available: boolean; problem: string | null };
}

export interface AudioLink {
  url: string;
  expiresAt: string;
}

/** SEC-3 — a registered machine, as the settings screen sees it. Never its token. */
export interface WorkerNode {
  id: string;
  name: string | null;
  status: "active" | "revoked";
  /** When it last asked for work. Null until it first connects. */
  lastHeartbeatAt: string | null;
  createdAt: string;
  revokedAt: string | null;
  /**
   * What the machine says about itself, reported on its claim poll (UI-13): `gpu`,
   * `os`, `worker`, `sidecar`, `steps`. Empty until it has asked for work once, which
   * is a real answer — a machine registered and never started has nothing to report.
   */
  machine: Record<string, string>;
  /** When the owner paused it (0068): it finishes what it holds and takes nothing new. */
  pausedAt?: string | null;
  /** Whether it holds a step right now. */
  busy?: boolean;
  /** The contract version it last reported. */
  protocol?: number;
  /** True when it is too old to be handed work, and needs updating. */
  needsUpdate?: boolean;
}

/** What registering returns, once, and never again. */
export interface IssuedWorker {
  worker: WorkerNode;
  token: string;
  shownOnce: boolean;
}

/** The two columns of the Open loops tab. */
export type OpenLoopDirection = "owed_by_me" | "owed_to_me";

export type OpenLoopAnswer = "done" | "dropped" | "reopen";

/**
 * One unfinished promise (AUTO-4).
 *
 * `overdue` is computed by the Server at read time rather than stored, because "overdue"
 * changes at midnight. `fulfilledBy` is a later insight that appears to close this — a
 * suggestion the owner confirms, never a closure the system performed.
 */
export interface OpenLoop {
  id: string;
  direction: OpenLoopDirection;
  statement: string;
  counterpartyId: string | null;
  counterparty: string | null;
  dueOn: string | null;
  overdue: boolean;
  insightId: string;
  sourceMaterialId: string;
  sourceTitle: string | null;
  fulfilledBy: string | null;
}

/**
 * One merge candidate the sweep found (AUTO-2).
 *
 * `similarity` is a trigram score to sort by, not a promise — the pass is fuzzy string
 * matching over names and aliases, and which of the two names should survive (if either)
 * is the owner's call, which is why accepting names `keepEntityId` explicitly rather than the
 * sweep picking a side.
 */
/**
 * WORLD-9 — a page synthesis opened with no entity behind it. Accepting creates the entity the
 * owner names on the card and attaches this page to it; rejecting means it is never raised
 * again.
 */
export interface EntitySuggestion {
  id: string;
  subjectId: string;
  slug: string;
  title: string;
  subjectKind: SubjectKind;
  summary: string | null;
  beliefCount: number;
  createdAt: string;
}

/** WORLD-10 — the org chart's material, as rows. The page builds the tree. */
export interface OrgChartPerson {
  id: string;
  name: string;
  role: string | null;
  isMe: boolean;
}

export interface OrgChartTeam {
  id: string;
  name: string;
  description: string | null;
}

export interface OrgChartLine {
  id: string;
  type: "reports_to" | "member_of" | "leads";
  subjectId: string;
  objectId: string;
  verdict: Verdict;
  assertedByUser: boolean;
}

export interface OrgChart {
  people: OrgChartPerson[];
  teams: OrgChartTeam[];
  lines: OrgChartLine[];
}

/** UI-20 — what the owner says about a belief they assert or correct. */
export interface BeliefAssertion {
  statement: string;
  supersedesBeliefId?: string | null;
  /** INS-1's closed list; omitted files it under Facts. */
  category?: InsightCategory | null;
  /** ISO date the belief started holding; omitted is now. */
  validFrom?: string | null;
}

export interface MergeSuggestion {
  id: string;
  entityAId: string;
  entityAName: string;
  entityBId: string;
  entityBName: string;
  entityType: string;
  similarity: number;
  createdAt: string;
}

// ------------------------------------------------------------------ chat (CHAT-2)

/**
 * One source an answer rested on.
 *
 * Recorded by the Server from what the grounding tools returned, never read out of the
 * answer's prose — which is why a chip can promise that what it names is really there.
 *
 * `altitude` is the distinction the whole system turns on: an `insight` is something a person
 * actually said in one meeting or document, a `wiki_section` is what the world view currently
 * holds after weighing many sources. The chip says which, because "Sanne said this" and "your
 * world view holds this" are different claims.
 */
export interface ChatCitation {
  searchDocumentId: string;
  altitude: "insight" | "wiki_section";
  /** What the chip reads: the section's heading, or the source an insight came from. */
  title: string;
  /** `slug#anchor` for a belief, the insight's id for a quote. */
  address: string;
  insightId: string | null;
  sourceMaterialId: string | null;
  sourceTitle: string | null;
  sourceDate: string | null;
  subjectSlug: string | null;
  sectionAnchor: string | null;
}

/** One turn as the thread shows it. Tool turns are the loop's bookkeeping and never arrive. */
export interface ChatTurn {
  id: string;
  sequence: number;
  role: "user" | "assistant";
  content: string | null;
  createdAt: string;
  citations: ChatCitation[];
}

export interface ChatConversation {
  id: string;
  title: string | null;
  lastMessageAt: string | null;
  createdAt: string;
}

/** What the Server sends while an answer is being written. One JSON object per line. */
export type ChatFrame =
  | { type: "text"; text: string }
  | { type: "done"; messageId: string; text: string; citations: ChatCitation[] }
  | { type: "error"; message: string };

/** What the MCP server asked the app to get approved (SEC-5). */
export interface AgentApproval {
  /** The agent, as it registered itself. */
  clientId: string;
  /** The opaque value the MCP server generated for this authorization request. Binding it
   *  into the assertion is what stops one approval being replayed into a different flow. */
  request: string;
  /** Where the browser goes next — checked by the Server against the MCP server's origin. */
  redirect: string;
  /** The tiers the consent screen displayed and the person approved (SEC-7). Sent as the
   *  screen parsed them rather than as the query string spelled them, so what is approved is
   *  what was shown; the Server validates them again and refuses any it does not offer. */
  scopes: string[];
}

export interface AgentApprovalResult {
  /** The short-lived assertion, carried back to the MCP server in the URL. */
  assertion: string;
  redirect: string;
  /** What the Server actually put its name to, which the MCP server then narrows again to
   *  what the agent asked for. */
  scopes: string[];
}

/**
 * An agent that can still reach this account's knowledge (SEC-7).
 *
 * Live means unrevoked and unexpired — the question the screen asks is "who can reach my
 * knowledge right now", not "who ever could". No token and no hash: enough to recognise an
 * agent and decide whether it should still be connected, and nothing more.
 */
export interface ConnectedAgent {
  clientId: string;
  clientName: string;
  /** The scope strings, unioned across this client's live tokens. */
  scopes: string[];
  connectedAt: string;
  lastUsedAt: string;
}

/**
 * One thing an agent did (`[MCP-CORE-6]`, M9.6).
 *
 * The path and never the query string or the body — the audit records which capability a
 * credential used, not what was said through it. `outcome` is `allowed`, `refused` or `failed`,
 * and the refusals are the rows worth reading: an agent reaching for something nobody granted
 * it is visible here and nowhere else.
 */
export interface AgentCall {
  method: string;
  path: string;
  outcome: "allowed" | "refused" | "failed";
  statusCode: number;
  createdAt: string;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Not a failure — the answer to "have I uploaded this before?".
 *
 * Modelled as a thrown error because it ends the upload the same way a refusal does,
 * and because the alternative — a success type the caller has to remember to check —
 * is how a duplicate quietly becomes a second copy.
 */
export class DuplicateUploadError extends ApiError {
  constructor(readonly duplicate: DuplicateSource) {
    super("You already uploaded this file.", 409);
    this.name = "DuplicateUploadError";
  }
}

/** Hands back the current access token, or null when nobody is signed in. */
export type AccessTokenSource = () => Promise<string | null>;

/**
 * The Server is the only thing the PWA talks to for data (ARCH-1); Supabase is used
 * for sign-in and nothing else. Every call carries the current access token, which is
 * asked for fresh each time rather than captured — supabase-js refreshes it in the
 * background and a captured copy goes stale mid-session.
 *
 * The token arrives through a function rather than through a Supabase client so this
 * class does not care where a valid token came from. That is what lets the test
 * sign-in (M14/SEC-6 removes it) work without a second code path through here.
 */
/**
 * The ten kinds of entity (UI-6). `person` is the one with a subtype behind it; `team` (M17.2)
 * is a unit inside the organization, where `member_of`, `leads` and a reporting line land.
 *
 * `term` is the one that is not a thing in the world but a word for one — "Common" for the
 * BI Common Dataset. It behaves like every other type here; the only difference lives on
 * the Server, where synthesis never opens a wiki page for one.
 */
export type EntityType =
  | "person"
  | "product"
  | "internal_product"
  | "service"
  | "feature"
  | "customer"
  | "supplier"
  | "location"
  | "event"
  | "initiative"
  | "team"
  | "term";

/**
 * UI-6 — one row of the organization's shared context, as the manager and detail read it.
 *
 * `role`, `isMe` and (implicitly) editing "me" only mean anything when `isPerson`; every
 * other kind carries a null role and is nobody. `mainGoal` and `secondaryGoals` are the same
 * shape for `isInitiative` (UI-6 amended): typed by a user directly, never inferred, and
 * null/empty for every other kind. `mentionCount` and `lastMentionedAt` stay at zero and
 * null until tagging fills them (M5.1) — a real answer, not a gap.
 */
export interface Entity {
  id: string;
  type: EntityType;
  name: string;
  description: string | null;
  aliases: string[];
  mentionCount: number;
  lastMentionedAt: string | null;
  isPerson: boolean;
  role: string | null;
  /** SPCH-7 — this person is the signed-in user. Exactly one person per org can be. */
  isMe: boolean;
  isInitiative: boolean;
  mainGoal: string | null;
  secondaryGoals: string[];
  createdAt: string;
  updatedAt: string;
}

/** UI-11 — one place an entity was tagged, with the source and the line it appeared on.
 * Empty for every entity until M5.1. */
export interface EntityMention {
  sourceMaterialId: string;
  sourceTitle: string | null;
  sourceKind: string;
  quote: string;
  createdAt: string;
}

/**
 * UI-19's one read: the entity as UI-6 already knows it, plus the world-view subject page it
 * stands for — null when nothing has synthesized or asserted anything on it yet. `slug` is
 * handed back even then, computed from the entity's current name, because it is what the
 * dossier's first "add a belief" mints the subject at.
 */
export interface EntityDossier {
  entity: Entity;
  slug: string;
  page: SubjectPage | null;
  git: WikiProvenance;
}

/** UI-11's detail payload: the entity and its mentions, fetched together. */
export interface EntityDetail {
  entity: Entity;
  mentions: EntityMention[];
}

// ---------------------------------------------------------------- people (UI-8)

/** UI-8 — one person as the gallery draws a card. `role` is the short label a person
 * carries everywhere they appear; the two counts are what the card shows. */
export interface PersonCard {
  id: string;
  name: string;
  role: string | null;
  isMe: boolean;
  meetingCount: number;
  insightCount: number;
  lastMeetingOn: string | null;
}

/** One recording the person was confirmed to have spoken in (SPCH-6). */
export interface PersonRecording {
  sourceMaterialId: string;
  title: string | null;
  kind: string;
  occurredOn: string | null;
}

/** One approved insight the person appears in, with the source it was read from. */
export interface PersonInsight {
  id: string;
  category: InsightCategory;
  text: string;
  sourceMaterialId: string;
  sourceTitle: string | null;
  sourceKind: string;
  sourceDate: string | null;
}

/** One source behind a relation — what the "N sources" disclosure lists. */
export interface RelationSource {
  insightId: string;
  sourceMaterialId: string;
  sourceTitle: string | null;
  sourceKind: string;
}

/**
 * The six verdicts a belief — and a relation, which is a belief too — can carry
 * (WORLD-1/WORLD-6). Declared here rather than in the page, because M7 reads the same
 * vocabulary off beliefs and two spellings of it would be one too many.
 */
export type Verdict = "open" | "extend" | "corroborate" | "weaken" | "supersede" | "challenge";

/**
 * UI-8's Relations panel: INS-5's source-scoped candidates, each stated by one insight, and
 * cross-source derivations (WORLD-6) in the same shape.
 * `removed` is a dismissal a member can undo, not a delete.
 */
export interface PersonRelation {
  id: string;
  subject: string;
  relationType: string;
  object: string;
  verdict: Verdict;
  removed: boolean;
  sources: RelationSource[];
  /** WORLD-8/UI-20 — drawn by the owner's own hand. */
  assertedByUser?: boolean;
}

/**
 * UI-8 — everything known about one person.
 *
 * `summary` is the cached generated paragraph and `description` is whatever a human wrote
 * on the entity; the dossier shows the human's when there is one. `summaryStale` means an
 * insight this person appears in was approved after the summary was written, and
 * `summaryJob` is what the queue is doing about that — a summary is written by a placed
 * `Reasoner` step like every other reasoning pass, on the GPU PC or in the cloud depending
 * on where the engine runs.
 */
export interface PersonDossier {
  id: string;
  name: string;
  role: string | null;
  description: string | null;
  isMe: boolean;
  aliases: string[];
  meetingCount: number;
  insightCount: number;
  firstSeenOn: string | null;
  summary: string | null;
  summaryGeneratedAt: string | null;
  summaryStale: boolean;
  summaryJob: "queued" | "running" | "failed" | null;
  relations: PersonRelation[];
  insights: PersonInsight[];
  recordings: PersonRecording[];
}

// ---------------------------------------------------------------- world view (UI-10, WORLD-3)

/**
 * The nine kinds a subject can be: the eight entity types, plus `topic` for a subject that
 * is not a thing the entity manager knows about — "the Q3 pricing decision" is a legitimate
 * subject with no entity behind it.
 */
export type SubjectKind = EntityType | "topic";

/** UI-10 — one subject as the index draws a card. */
export interface SubjectCard {
  id: string;
  slug: string;
  title: string;
  kind: SubjectKind;
  summary: string | null;
  entityId: string | null;
  /** Lights the "Dossier" pill, and only ever true when `entityId` is a person. */
  isPerson: boolean;
  beliefCount: number;
  updatedAt: string;
}

/** UI-10's index, plus the two numbers the header and the navigator show. */
export interface WorldViewIndex {
  subjects: SubjectCard[];
  contradictionCount: number;
  lastRewrittenAt: string | null;
}

/** UI-23 — a pass in flight, seen from outside it. */
export interface RunningSynthesisBatch {
  batchId: string;
  insightCount: number;
  /** 1-based, out of `stepCount`. */
  stepIndex: number;
  stepCount: number;
  /** The worker's own name, or null while the step is claimed but not yet picked up. */
  runningOn: string | null;
  startedAt: string;
}

/** UI-23 — the most recent pass that is no longer running. */
export interface LastSynthesisBatch {
  completedAt: string;
  verdictCount: number;
  failed: boolean;
}

/** UI-23 — what the World view's status line and an approval's follow-up read. */
export interface WorldViewActivity {
  running: RunningSynthesisBatch | null;
  waitingCount: number;
  lastBatch: LastSynthesisBatch | null;
}

// ---------------------------------------------------------------- insight processing (UI-24)

/** UI-24 — one batch, as the processing view's list draws a row. */
export interface SynthesisBatchListRow {
  id: string;
  createdAt: string;
  completedAt: string | null;
  batchStatus: "running" | "done" | "failed";
  runStatus: "queued" | "processing" | "done" | "failed" | "cancelled";
  runId: string;
  /** The run's own first not-done step, by sequence — null once every step is done. */
  currentStep: string | null;
  /** The latest failed step's own error sentence, when the run has one. */
  error: string | null;
  insightCount: number;
  appliedCount: number;
  /** How many entries the model proposed and the parser refused. Zero for a clean answer,
   * and for every batch predating this view — that is "no data", not a verified zero. */
  droppedCount: number;
  engine: string;
  promptVersion: string | null;
  costUsd: number | null;
  commitSha: string | null;
}

/** UI-24 — recent batches plus the backlog waiting for the next one (WORLD-11). */
export interface SynthesisBatchList {
  batches: SynthesisBatchListRow[];
  waitingCount: number;
}

/** UI-24 — one insight the batch read, with what it did. Empty `verdicts` means "no
 * change", a legitimate answer rather than a gap. */
export interface SynthesisBatchInsightView {
  id: string;
  subject: string | null;
  category: InsightCategory;
  text: string;
  sourceMaterialId: string;
  sourceTitle: string | null;
  sourceKind: string;
  sourceDate: string | null;
  verdicts: Verdict[];
}

/**
 * UI-24 — one verdict actually applied, read from `belief_verdicts` rather than the model's
 * raw answer, because that is the row every other read already treats as the record of what
 * happened. `reasoning` is stored on every verdict, and this is the one place the app
 * shows it.
 */
export interface SynthesisBatchVerdictView {
  verdictId: string;
  insightId: string;
  verdict: Verdict;
  subjectSlug: string;
  subjectTitle: string;
  beliefId: string;
  statement: string;
  confidenceNow: number | null;
  /** The belief's confidence as the candidate pool held it before this pass ran. Null for a
   * belief this verdict opened — there is no "before" for a belief that did not exist yet. */
  confidenceBefore: number | null;
  reasoning: string | null;
  assertedByUser: boolean;
  /** Set for a `supersede`: the belief this one closed. */
  supersededBeliefId: string | null;
  supersededStatement: string | null;
  /** Set for a `challenge`: the pair it filed and the belief on the other side. */
  contradictionId: string | null;
  contradictionStatus: string | null;
  contradictsBeliefId: string | null;
}

/** UI-24 — one belief in the candidate pool, as it stood before this pass ran. */
export interface SynthesisBatchCandidateBelief {
  id: string;
  statement: string;
  confidence: number | null;
  since: string | null;
}

/** UI-24 — one subject the model was shown, from the `synthesize` step's own input
 * artifact — not the world view as it stands now. */
export interface SynthesisBatchCandidateSubject {
  slug: string;
  title: string;
  kind: string;
  summary: string | null;
  touched: boolean;
  beliefs: SynthesisBatchCandidateBelief[];
}

/** UI-24 — one relation this batch's insights are evidence for (WORLD-6). */
export interface SynthesisBatchRelation {
  id: string;
  type: string;
  verdict: Verdict;
  confidence: number | null;
  subjectName: string;
  subjectKind: string;
  objectName: string;
  objectKind: string;
  validTo: string | null;
}

/** UI-24 — one commitment this batch's `track_commitments` step filed. */
export interface SynthesisBatchCommitment {
  id: string;
  statement: string;
  direction: "owed_by_me" | "owed_to_me";
  dueOn: string | null;
  status: "open" | "done" | "dropped";
  counterpartyName: string | null;
}

/**
 * UI-24 — one entry the model proposed and `SynthesisPrompt.Parse` refused to carry
 * forward. Ids and reason codes only, never the model's own prose — that text lives in the
 * model exchange, admin-gated below.
 */
export interface DroppedVerdict {
  insightId: string | null;
  verdict: string | null;
  beliefId: string | null;
  subject: string | null;
  reason: "unknown_verdict" | "unknown_insight" | "unknown_belief" | "no_subject" | "no_statement";
}

/** UI-24 — everything one batch's page shows. */
export interface SynthesisBatchDetail {
  id: string;
  createdAt: string;
  completedAt: string | null;
  batchStatus: "running" | "done" | "failed";
  runStatus: "queued" | "processing" | "done" | "failed" | "cancelled";
  engine: string;
  promptVersion: string | null;
  costUsd: number | null;
  commitSha: string | null;
  steps: PipelineStep[];
  insightsRead: number;
  proposed: number;
  keptByParser: number;
  applied: number;
  verdictCounts: Partial<Record<Verdict, number>>;
  insights: SynthesisBatchInsightView[];
  verdicts: SynthesisBatchVerdictView[];
  dropped: DroppedVerdict[];
  candidatePool: SynthesisBatchCandidateSubject[];
  relations: SynthesisBatchRelation[];
  commitments: SynthesisBatchCommitment[];
}

/**
 * UI-24 — one model call in full: the prompt, the raw answer, why it stopped, the tokens and
 * the timing (`reasoner_calls`, 0067). Owner/admin only — a full transcript of what the
 * organization said, not a number.
 */
export interface ReasonerExchangeView {
  id: string;
  occurredAt: string;
  placement: "cloud" | "worker";
  model: string;
  systemPrompt: string;
  userPrompt: string;
  responseSchema: string | null;
  maxOutputTokens: number | null;
  response: string | null;
  responseTruncated: boolean;
  thinking: string | null;
  stopReason: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  durationMs: number | null;
  error: string | null;
  /** The provider stopped at the output ceiling rather than finishing. */
  wasCutOff: boolean;
}

/** One insight behind a belief, with the verdict it landed (UI-7's world-view direction). */
export interface BeliefInsight {
  id: string;
  verdict: Verdict;
  category: InsightCategory;
  text: string;
  sourceMaterialId: string;
  sourceTitle: string | null;
  sourceKind: string;
  sourceDate: string | null;
}

/**
 * One belief. `validTo` null means it is currently held; a closed window carries the belief
 * that replaced it, which is on another subject exactly when the supersession was a rename
 * (WORLD-1, and the 2026-08-04 wiki-identity decision).
 */
export interface SubjectBelief {
  id: string;
  statement: string;
  confidence: number | null;
  category: InsightCategory;
  validFrom: string;
  validTo: string | null;
  supersededById: string | null;
  supersededBySlug: string | null;
  supersededByTitle: string | null;
  supersededByStatement: string | null;
  contradicted: boolean;
  insights: BeliefInsight[];
  /** WORLD-8 — true when the verdict that opened this belief came from a user directly
   * rather than an insight; what lets it show as "asserted by you" rather than "from a
   * source", and the same flag a machine pass may only `challenge`, never silently close. */
  assertedByUser: boolean;
}

/**
 * A run of beliefs under one heading. `anchor` comes from the Server rather than being
 * derived here, because it is the same string the wiki emits and `slug#anchor` is a
 * permanent address that search, chat citations and the MCP tools all store.
 */
export interface SubjectSection {
  category: InsightCategory;
  heading: string;
  anchor: string;
  beliefs: SubjectBelief[];
}

export interface SubjectSource {
  sourceMaterialId: string;
  title: string | null;
  kind: string;
  occurredOn: string | null;
  insightCount: number;
}

export interface RelatedSubject {
  slug: string;
  title: string;
  kind: SubjectKind;
  sharedSources: number;
}

/**
 * One of this subject's relations to another entity (WORLD-6/WORLD-7), when the subject
 * stands for one. `isSubject` is true when this subject's entity is the relation's subject
 * end ("Jan **leads** Falcon" on Jan's page) and false when it is the object end (the same
 * relation on Falcon's page) — the direction the sentence reads in.
 */
export interface SubjectRelation {
  id: string;
  type: string;
  verdict: Verdict;
  confidence: number | null;
  isSubject: boolean;
  otherEntityId: string;
  otherEntityName: string;
  otherEntityKind: EntityType;
  sourceCount: number;
  /** WORLD-8 — true when this relation was asserted by a user directly rather than derived. */
  assertedByUser: boolean;
}

/** One synthesis batch that touched this subject — the right rail's Page history. */
export interface SubjectRevision {
  batchId: string;
  at: string;
  commitSha: string | null;
  verdictCount: number;
  /** UI-23 */
  insightCount: number;
  /** UI-23 — read from ARCH-13's ledger; absent rather than 0 for a batch that made no
   * cloud call. */
  costUsd: number | null;
}

export interface SubjectPage {
  id: string;
  slug: string;
  title: string;
  kind: SubjectKind;
  summary: string | null;
  entityId: string | null;
  isPerson: boolean;
  updatedAt: string;
  sections: SubjectSection[];
  superseded: SubjectBelief[];
  sources: SubjectSource[];
  related: RelatedSubject[];
  history: SubjectRevision[];
  /** Repository-relative, the way the design's right rail writes it. */
  wikiPath: string;
  /** WORLD-7 — empty when the subject is not entity-backed. */
  relations: SubjectRelation[];
  /** WORLD-9 — the entity's own description, verbatim: the About section, rendered first. The
   * generated `summary` is shown only where this is null. */
  about?: string | null;
}

/** WORLD-5 — where the world view is published. `remote` is null: the repository is local. */
export interface WikiProvenance {
  remote: string | null;
  commitSha: string | null;
  committedAt: string | null;
}

export interface SubjectPageResponse {
  page: SubjectPage;
  git: WikiProvenance;
}

// ---------------------------------------------------------------- contradictions (AUTO-1)

/** One side of a contradiction, as the review queue's card draws it. */
export interface ContradictionBelief {
  id: string;
  statement: string;
  confidence: number | null;
  validFrom: string;
  /** Null only for a belief no verdict produced, which the pipeline cannot create. */
  sourceMaterialId: string | null;
  sourceTitle: string | null;
  sourceKind: string | null;
  sourceDate: string | null;
  insightId: string | null;
}

/**
 * One contradiction waiting on the owner: two current beliefs on one subject that disagree.
 *
 * `origin` is `challenge` when an insight disputed a belief and synthesis could not tell which
 * won, or `sweep` when the scheduled pass found two beliefs that had drifted apart across
 * separate batches with nothing ever challenging either. `batchId` names the batch behind a
 * challenge and is null for a sweep, which belongs to no batch.
 */
export interface Contradiction {
  id: string;
  origin: "challenge" | "sweep";
  status: string;
  subjectSlug: string;
  subjectTitle: string;
  createdAt: string;
  batchId: string | null;
  beliefA: ContradictionBelief;
  beliefB: ContradictionBelief;
}

/** The owner's three answers to a contradiction (AUTO-1). */
export type ContradictionAction = "acknowledge" | "keep" | "discard";

/** How many things are waiting on the owner, split by what kind of thing they are. */
export interface ReviewCounts {
  /** The full-mode rail badge: both kinds the Review queue holds. */
  count: number;
  insights: number;
  contradictions: number;
  /** SPCH-8's undecided proposals. Answered on Transcript Review, or on a simple card. */
  corrections: number;
  /** SPCH-6's unnamed voices. Answered on a recording's Speakers tab, or on a simple card. */
  speakers: number;
  /**
   * UI-16's rail badge, and deliberately a different number from {@link count}.
   *
   * Simple mode's Needs you merges all four kinds into one stream; full mode's Review
   * queue holds only the first two, because corrections are answered where the
   * surrounding lines are and voices where the recording is. Two screens, two counts,
   * each promising exactly what the screen it leads to has on it.
   */
  needsYou: number;
  /**
   * UI-21's questions (M17.3) — the fifth kind. Counted in both badges, because both the Review
   * queue and Needs you now hold them. Optional only so a response from a Server that predates
   * M17.3 still reads.
   */
  questions?: number;
}

/** The six gaps the system asks about (UI-21). Each has exactly one answer shape. */
export type QuestionKind =
  | "subject_entity"
  | "person_role"
  | "person_team"
  | "initiative_lead"
  | "commitment_counterparty"
  | "speaker_person";

/**
 * UI-21 — one thing the system is asking the owner, because it noticed a gap. Answered by the
 * write that fills the gap, through the route a page already uses; the Server's sweep notices,
 * and the question is gone on the next read.
 */
export interface OwnerQuestion {
  id: string;
  kind: QuestionKind;
  /** The subject, entity, open loop or voice it is about, by kind. */
  targetId: string;
  /** The name the question is about. */
  name: string;
  /** The question in one sentence, in the Server's words so the app and an agent agree. */
  question: string;
  detail: string | null;
  /** What synthesis thought a page was, or the entity's type. */
  entityKind: string | null;
  subjectSlug: string | null;
  /** The WORLD-9 suggestion a `subject_entity` card accepts. */
  suggestionId: string | null;
  /** The recording a `speaker_person` voice was confirmed in. */
  recordingId: string | null;
  sourceTitle: string | null;
  createdAt: string;
}

/**
 * SPCH-8 — one correction the Reasoner proposed and nobody has decided yet, read across
 * every recording rather than from inside one (UI-16).
 *
 * It carries its recording because that is the address the answer is sent to: the resolve
 * route is scoped to the recording as well as to the correction, deliberately, so a client
 * may only write to an address that names what it opened.
 */
export interface PendingCorrection {
  id: string;
  sourceMaterialId: string;
  sourceTitle: string | null;
  sourceDate: string | null;
  sourceSegmentId: string;
  /** Where in the recording the line is, for a link that lands on it. */
  startsAtMs: number | null;
  originalText: string;
  correctedText: string;
  category: string | null;
  reasoning: string | null;
  createdAt: string;
}

/**
 * SPCH-6 — one voice the machine has had its go at and could not name, read across every
 * recording (UI-16).
 *
 * The sample turn travels with it because the card is built round listening: a fragment to
 * play, and the words that were said in it. `matchedPerson*` is the gallery's proposal and
 * nothing more — the same suggestion, with the same score, that the recording's own
 * Speakers tab pre-fills.
 */
export interface UnnamedSpeaker {
  id: string;
  sourceMaterialId: string;
  sourceTitle: string | null;
  sourceDate: string | null;
  label: string | null;
  segmentCount: number;
  speakingMs: number;
  sampleSegmentId: string | null;
  sampleStartMs: number | null;
  sampleEndMs: number | null;
  sampleText: string | null;
  matchedPersonId: string | null;
  matchedPersonName: string | null;
  matchScore: number | null;
  createdAt: string;
}

// ---------------------------------------------------------------- the ledger (UI-9)

/** One belief a single insight moved, as a ledger row's "What it changed" lists it. */
export interface LedgerEffect {
  beliefId: string;
  verdict: Verdict;
  subjectSlug: string;
  subjectTitle: string;
  /** The heading the belief lives under on its subject page. */
  section: string;
  /** That heading's anchor — the same string the wiki file and the page's "On this page" use. */
  anchor: string;
  statement: string;
  confidence: number | null;
  /** False once a later batch closed the belief's window. */
  current: boolean;
}

/** One row of the ledger: what an insight says, where it came from, what it changed. */
export interface LedgerRow {
  id: string;
  reviewState: "pending" | "approved" | "discarded";
  category: InsightCategory;
  text: string;
  subject: string | null;
  grounded: boolean;
  sourceMaterialId: string;
  sourceTitle: string | null;
  sourceKind: string;
  sourceDate: string | null;
  createdAt: string;
  effects: LedgerEffect[];
}

/** One filter option and how many rows are behind it. */
export interface LedgerFacet {
  id: string;
  count: number;
}

export interface LedgerSourceFacet {
  id: string;
  title: string | null;
  kind: string;
  count: number;
}

/** The three-card funnel: what came in, what survived review, what it added up to. */
export interface LedgerFunnel {
  sources: number;
  approvedInsights: number;
  beliefsChanged: number;
  subjects: number;
}

/**
 * Everything the Insights destination draws, from one read — the rows, every filter control's
 * count, the funnel and the resolved date range. One answer so the navigator panel and the
 * page can never disagree about how many of anything there are.
 */
export interface LedgerView {
  rows: LedgerRow[];
  total: number;
  horizons: LedgerFacet[];
  states: LedgerFacet[];
  verdicts: LedgerFacet[];
  sources: LedgerSourceFacet[];
  funnel: LedgerFunnel;
  /** Null for "all time", which is a window with no beginning rather than a guessed one. */
  from: string | null;
  to: string;
}

/** What the ledger is being asked for. Every field ends up in a `WHERE` clause. */
export interface LedgerQuery {
  horizon: string;
  state: string;
  /** Null for no verdict filter — which is not the same as "had some effect". */
  verdict: string | null;
  source: string | null;
  text: string | null;
}

// ---------------------------------------------------------------- insights (INS-3, INS-4)

/** The closed list of nine categories every insight carries (INS-1, INS-6). */
export type InsightCategory =
  | "decision"
  | "commitment"
  | "deadline"
  | "fact"
  | "preference"
  | "request"
  | "requirement"
  | "update"
  | "question"
  | "product_improvement";

/** One entity an insight mentions (INS-2), as a card draws its chip. */
export interface InsightEntityRef {
  id: string;
  name: string;
  type: EntityType;
}

/** One candidate relation an insight states (INS-5), as a card lists it. */
export interface InsightRelationRef {
  subject: string;
  subjectType: EntityType;
  object: string;
  objectType: EntityType;
  relationType: string;
}

/** One expression grounding resolved and what it resolved to (INS-1). */
export interface InsightGroundingRef {
  expression: string;
  referent: string;
}

/**
 * One reason an insight is not grounded (INS-1), so the card can name what to fix.
 * `expression` is the words at fault — absent for `no_subject` and `unspecified`;
 * `referent` is, for `not_written_in`, what the extractor said they meant and then did not
 * write into the sentence.
 */
export interface InsightUngroundedReason {
  kind: "unnamed_speaker" | "unresolved" | "unspecified" | "no_subject" | "generic_subject" | "not_written_in";
  expression: string | null;
  referent: string | null;
}

/**
 * INS-4 — one pending insight, as the review queue draws its card.
 *
 * `composedText` and `normalizedText` are the two halves of "How this was read": the
 * self-contained insight the extractor composed, then the same thing normalized to
 * English (INS-1). `quotes` are the source lines it was drawn from; `entities` and
 * `relations` are the tags and candidate relations approving it would accept in one action.
 *
 * `subject`, `grounded` and `grounding` are INS-1's grounding stage. An insight is read
 * here on its own, and everywhere afterwards on its own — so "the system will scan for JSON
 * data blobs" is true inside the meeting and useless outside it. `grounded: false` means
 * the extractor could not name something the sentence leans on; the card says so, and
 * improving the text is what clears the flag. `ungroundedReasons` says what could not be
 * named, one entry per thing to fix; it is empty (or absent) for an insight flagged before the
 * reason was recorded, and the card falls back to saying "something". Since INS-7 the queue no longer sorts by it: it
 * keeps one source's insights together, in the order they were read out of it.
 *
 * `sourceAuthoredByOwner` is CAP-8 — the source is the owner's own words.
 */
export interface ReviewInsight {
  id: string;
  sourceMaterialId: string;
  sourceTitle: string | null;
  sourceKind: "recording" | "chat_log" | "document" | "note";
  sourceDate: string | null;
  category: InsightCategory;
  composedText: string;
  normalizedText: string;
  subject: string | null;
  grounded: boolean;
  grounding: InsightGroundingRef[];
  ungroundedReasons?: InsightUngroundedReason[] | null;
  createdAt: string;
  quotes: string[];
  entities: InsightEntityRef[];
  relations: InsightRelationRef[];
  sourceAuthoredByOwner?: boolean;
}

/** INS-7 — what approving a source at once did. */
export interface SourceApproval {
  approved: number;
  embedded: number;
  synthesizing: boolean;
}

/** One insight on the recording detail's Insights tab (UI-5). */
export interface RecordingInsight {
  id: string;
  sourceOrdinal: number;
  category: InsightCategory;
  composedText: string;
  normalizedText: string;
  reviewState: "pending" | "approved" | "discarded";
  entities: InsightEntityRef[];
}

/** UI-5 — the recording detail's Insights and Entities tabs in one payload. */
export interface RecordingInsights {
  insights: RecordingInsight[];
  /** The distinct entities across the recording's insights, for the Entities tab. */
  entities: InsightEntityRef[];
}

/**
 * UI-7 — one source span an insight was drawn from.
 *
 * `sourceSegmentId` is the segment to highlight, and `startOffset`/`endOffset` the
 * character range within its effective (corrected) text — null offsets mean the whole
 * segment. The offsets index into the same text the Transcript tab renders, because
 * extraction composed the insight over exactly that text (INS-1), so the page highlights
 * `segment.text.slice(startOffset, endOffset)` in place.
 */
export interface InsightSpan {
  sourceMaterialId: string;
  sourceSegmentId: string;
  sequence: number;
  startOffset: number | null;
  endOffset: number | null;
}

/**
 * SRCH-1 — one search result, at one of exactly two altitudes.
 *
 * `altitude` is on every result on purpose (SRCH-3): the reader always knows whether they
 * are looking at one thing somebody said in one meeting, or at what the system now believes
 * after weighing many of them. Every field below it belongs to one altitude and is null on
 * the other — an insight carries the source it was read from, a wiki section carries the
 * subject page and the anchor it opens at.
 */
export interface SearchResult {
  id: string;
  altitude: "insight" | "wiki_section";
  title: string;
  snippet: string;
  score: number;
  insightId: string | null;
  sourceMaterialId: string | null;
  sourceTitle: string | null;
  sourceKind: "recording" | "chat_log" | "document" | "note" | null;
  sourceDate: string | null;
  category: InsightCategory | null;
  wikiPath: string | null;
  sectionAnchor: string | null;
  subjectSlug: string | null;
  subjectTitle: string | null;
  subjectKind: SubjectKind | null;
}

/** SRCH-3 — the three scopes, spelled as `user_settings.defaultSearchScope` spells them. */
export type SearchScope = "world" | "insights" | "both";

/**
 * What a search asks for: the words, the altitude scope (SRCH-3) and the four filters
 * (SRCH-2).
 *
 * Everything but the query is optional, and every omission means the widest answer — both
 * altitudes, nobody in particular, no date bound. That is what makes a reset a request with
 * nothing in it rather than one that has to remember to say "both".
 *
 * `currentBeliefsOnly` is on by default (WORLD-1), so leaving it out means true and only an
 * explicit `false` brings superseded beliefs back.
 */
export interface SearchQuery {
  query: string;
  scope?: SearchScope;
  personId?: string | null;
  entityId?: string | null;
  /** ISO dates, on the source material's own date rather than when it was uploaded. */
  from?: string | null;
  to?: string | null;
  currentBeliefsOnly?: boolean;
}

/**
 * What one search did.
 *
 * `mode` is reported rather than assumed, and all three values are normal:
 * `hybrid+rerank` is the full path, `hybrid` means no reranking model is provisioned on the
 * Server yet, and `lexical` means the vector arm contributed nothing — which is what an
 * unprovisioned `Embedder` looks like from here. `total` is the size of the candidate pool
 * that was ranked, which can exceed `results.length`.
 */
export interface SearchAnswer {
  query: string;
  mode: "hybrid+rerank" | "hybrid" | "lexical";
  total: number;
  results: SearchResult[];
}

/**
 * SRCH-4 — one quote a transcript search found: a line as it reads now (a standing correction
 * wins), who said it, and where. Not an altitude and not approved by anybody — a record of what
 * was said, pending review or not.
 */
export interface TranscriptHit {
  segmentId: string;
  sourceMaterialId: string;
  sourceKind: "recording" | "chat_log" | "document" | "note" | "meeting";
  sourceTitle: string | null;
  sourceDate: string | null;
  parentSourceMaterialId: string | null;
  speakerId: string | null;
  /** Null for a note paragraph or a document block, which nobody spoke. */
  speakerName: string | null;
  personId: string | null;
  sequence: number;
  startsAtMs: number | null;
  endsAtMs: number | null;
  text: string;
  rank: number;
  /**
   * The words of `text` the search matched, as written there. A search finds a word's Dutch and
   * English forms, so these are what to mark, not the typed words. Absent from an older Server.
   */
  matchedWords?: string[];
}

/** What a transcript search narrows by — the person who spoke, one source, the sources' dates. */
export interface TranscriptQuery {
  query: string;
  personId?: string | null;
  sourceMaterialId?: string | null;
  from?: string | null;
  to?: string | null;
  limit?: number;
}

export interface TranscriptSearchAnswer {
  query: string;
  results: TranscriptHit[];
}

export class ApiClient {
  constructor(
    private readonly baseUrl: string,
    private readonly accessToken: AccessTokenSource,
  ) {}

  async getMe(): Promise<Me> {
    return this.request<Me>("GET", "/api/me");
  }

  async updateSettings(patch: Partial<UserSettings>): Promise<UserSettings> {
    return this.request<UserSettings>("PATCH", "/api/me/settings", patch);
  }

  /**
   * UI-18 — save the organization's general information.
   *
   * A whole write, not a patch: blank clears, which is what lets a description somebody
   * no longer wants actually go away. Owners and admins only — a member gets a 403.
   */
  async updateOrganizationProfile(
    id: string,
    profile: OrganizationProfileEdit,
  ): Promise<Organization> {
    return this.request<Organization>("PUT", `/api/organizations/${id}/profile`, profile);
  }

  /** The capture pages' "Recently added" — every kind of source, newest upload first. */
  async listSources(limit = 10): Promise<SourceMaterial[]> {
    return this.request<SourceMaterial[]>("GET", `/api/sources?limit=${limit}`);
  }

  /**
   * CAP-7 — a quick note. It becomes a `note` source and goes straight to insight
   * extraction (INS-1): there is no file and no audio pipeline, so unlike `upload` this
   * is an ordinary JSON POST rather than a streamed multipart with progress.
   */
  async createNote(input: {
    title?: string;
    body: string;
    sourceDate?: string;
    language?: string;
    /** CAP-8 — the note is the caller's own words. */
    authoredByMe?: boolean;
  }): Promise<SourceMaterial> {
    return this.request<SourceMaterial>("POST", "/api/sources/notes", input);
  }

  /**
   * UI-4 — every recording, with the status the pipeline has got it to.
   *
   * The whole list, not a page of it: the in-page search and the navigator panel's
   * "All recordings" both read it, and neither works on a slice. The Server caps it,
   * and paging is a problem for whenever there are enough recordings to have one.
   */
  async listRecordings(): Promise<Recording[]> {
    return this.request<Recording[]>("GET", "/api/recordings");
  }

  async getSource(id: string): Promise<SourceMaterial> {
    return this.request<SourceMaterial>("GET", `/api/sources/${id}`);
  }

  /** Every meeting split off one recording, oldest first. */
  async listMeetings(recordingId: string): Promise<Meeting[]> {
    return this.request<Meeting[]>("GET", `/api/recordings/${recordingId}/meetings`);
  }

  /**
   * Cut a meeting in two at `fromSequence` — the same `source_segments.sequence`
   * numbering the Transcript tab reads by. Everything from that point on becomes a new,
   * named sibling meeting; the meeting split keeps everything before it. Returns the new,
   * later half.
   */
  async splitMeeting(
    meetingId: string,
    input: { title: string; description?: string; fromSequence: number },
  ): Promise<Meeting> {
    return this.request<Meeting>("POST", `/api/meetings/${meetingId}/split`, input);
  }

  /** SPCH-13 — the meetings the audio phase proposes for a recording. */
  async getMeetingProposal(recordingId: string): Promise<MeetingProposal> {
    return this.request<MeetingProposal>("GET", `/api/recordings/${recordingId}/meeting-proposal`);
  }

  /**
   * SPCH-13 — replace the recording's meetings with the proposal. One group per resulting
   * meeting (or discard), each naming the index of the proposal stretch it starts at — a group
   * spanning several stretches is a merge, and `startIndex` is how the Server knows where a
   * group starts without trusting a client-supplied time. A part cut or moved by hand in review
   * (SPCH-14) names the transcript line it starts at as `fromSequence` instead. Answers with the
   * recording's meetings as they now are.
   */
  async applyMeetingProposal(
    recordingId: string,
    groups: AdoptedProposalGroup[],
    replaceStarted = false,
  ): Promise<Meeting[]> {
    return this.request<Meeting[]>("POST", `/api/recordings/${recordingId}/meeting-proposal/apply`, {
      groups,
      replaceStarted,
    });
  }

  /** Rename or describe a meeting. Blank clears either field. */
  async updateMeeting(
    meetingId: string,
    input: { title?: string | null; description?: string | null },
  ): Promise<Meeting> {
    return this.request<Meeting>("PATCH", `/api/meetings/${meetingId}`, input);
  }

  /** CAP-11 — rename a recording. A blank title clears back to the recorder's own name. */
  async renameRecording(id: string, title: string | null): Promise<SourceMaterial> {
    return this.request<SourceMaterial>("PATCH", `/api/recordings/${id}`, { title });
  }

  /**
   * CAP-4 — permanently delete a source: the file, its pipeline artifacts and everything
   * derived from it. Insights survive only where another source still evidences them. Rare
   * and irreversible, so the caller confirms first — provenance is usually worth keeping.
   */
  async deleteSource(id: string): Promise<void> {
    await this.request("DELETE", `/api/sources/${id}`);
  }

  /**
   * The newest run over a source, or null when there is none.
   *
   * Null is a real answer rather than a gap: a document has no chain until M5.1, and a
   * recording uploaded before the queue existed never got a run.
   */
  async getPipeline(id: string): Promise<PipelineRun | null> {
    return this.request<PipelineRun | null>("GET", `/api/sources/${id}/pipeline`);
  }

  /** Queue a source that has no run yet. Restarting at a step is `restartPipeline`. */
  async startPipeline(id: string): Promise<PipelineRun> {
    return this.request<PipelineRun>("POST", `/api/sources/${id}/pipeline`);
  }

  /**
   * SPCH-9 — stop at the current step boundary.
   *
   * The step already running finishes; nothing after it is handed out. The run comes
   * back reading `cancelling` until that last step lands.
   */
  async cancelPipeline(id: string): Promise<PipelineRun> {
    return this.request<PipelineRun>("POST", `/api/sources/${id}/pipeline/cancel`);
  }

  /**
   * SPCH-10 — run it again from a step, throwing away everything from there on.
   *
   * Without `discardReview` the Server refuses when a restart would rebuild the
   * transcript that somebody has flagged, corrected or marked reviewed — and says what
   * would be lost. That refusal is the feature: the alternative is finding out
   * afterwards, on a recording somebody spent an evening reading.
   */
  async restartPipeline(id: string, stepName: string, discardReview = false): Promise<PipelineRun> {
    return this.request<PipelineRun>("POST", `/api/sources/${id}/pipeline/restart`, {
      stepName,
      discardReview,
    });
  }

  /** ARCH-8 — what a step was given and what it produced. */
  async getStepArtifacts(id: string, stepId: string): Promise<StepArtifact[]> {
    return this.request<StepArtifact[]>("GET", `/api/sources/${id}/pipeline/steps/${stepId}/artifacts`);
  }

  /** UI-13 — what still stops a recording running end to end, derived live. */
  async getSetup(): Promise<SetupState> {
    return this.request<SetupState>("GET", "/api/setup");
  }

  // ---------------------------------------------------------------- entities (UI-6, UI-11, SPCH-7)

  /** Every entity in the organization — the manager filters and sorts the whole set in
   * the browser, and the navigator counts read the same list. */
  async listEntities(): Promise<Entity[]> {
    return this.request<Entity[]>("GET", "/api/entities");
  }

  /** UI-11 — one entity and its mentions. */
  async getEntity(id: string): Promise<EntityDetail> {
    return this.request<EntityDetail>("GET", `/api/entities/${id}`);
  }

  /** Add an entity; when its type is `person`, the Server makes the subtype row too. */
  /** `featureOf` (0072) — required for a feature, refused for every other kind: the product,
   * internal product or service it is a feature of, linked in the same transaction. */
  async createEntity(
    input: { type: EntityType; name: string; description: string; featureOf?: string },
  ): Promise<Entity> {
    return this.request<Entity>("POST", "/api/entities", input);
  }

  /** `mainGoal` only ever lands on an initiative (UI-6 amended); the Server no-ops it for
   * every other kind, so a plain rename can always send it as whatever the entity already had. */
  async updateEntity(
    id: string, patch: { name: string; description: string; mainGoal?: string | null },
  ): Promise<Entity> {
    return this.request<Entity>("PATCH", `/api/entities/${id}`, patch);
  }

  async deleteEntity(id: string): Promise<void> {
    await this.request<null>("DELETE", `/api/entities/${id}`);
  }

  /**
   * The chips send the whole set on every add or remove; the Server reconciles.
   *
   * Throws an `ApiError` with status 409 when one of the words is already how this
   * organization refers to something else (0035) — its message names what holds it, and is
   * written to be shown to somebody as it stands.
   */
  async setEntityAliases(id: string, aliases: string[]): Promise<Entity> {
    return this.request<Entity>("PUT", `/api/entities/${id}/aliases`, { aliases });
  }

  /** UI-6 amended — an initiative's secondary goals, sent as the whole ordered list on
   * every add, remove or reorder; the Server reconciles the same way `setEntityAliases` does. */
  async setInitiativeSecondaryGoals(id: string, goals: string[]): Promise<Entity> {
    return this.request<Entity>("PUT", `/api/entities/${id}/secondary-goals`, { goals });
  }

  /** SPCH-7 — mark this person as the signed-in user. Moves the link off whoever held it. */
  async setOwner(id: string): Promise<Entity> {
    return this.request<Entity>("POST", `/api/entities/${id}/me`);
  }

  // ---------------------------------------------------------------- initiative dossier (UI-19)

  /** UI-19 — the entity and the world-view subject page it stands for, fetched together. */
  async getEntityDossier(id: string): Promise<EntityDossier> {
    return this.request<EntityDossier>("GET", `/api/entities/${id}/dossier`);
  }

  /**
   * WORLD-8 — open, extend or supersede a belief on this entity's subject directly.
   * `supersedesBeliefId` null means open/extend; naming a belief means supersede.
   * Throws an `ApiError` with status 400 when the named belief is not open on this subject.
   */
  async assertBelief(entityId: string, input: BeliefAssertion): Promise<{ beliefId: string; slug: string }> {
    return this.request("POST", `/api/entities/${entityId}/beliefs`, input);
  }

  /** UI-20 — the same assertion on a World view page by its slug, which is how a `topic` page
   * with no entity behind it takes one. */
  async assertSubjectBelief(slug: string, input: BeliefAssertion): Promise<{ beliefId: string; slug: string }> {
    return this.request("POST", `/api/world-view/${encodeURIComponent(slug)}/beliefs`, input);
  }

  /** WORLD-9 — ask for the generated paragraph on any entity with a page. */
  async refreshEntitySummary(id: string): Promise<void> {
    await this.request<{ queued: boolean }>("POST", `/api/entities/${id}/summary`);
  }

  /**
   * WORLD-8 — assert a relation between this entity and another directly. `thisIsSubject`
   * says which end of the sentence this entity is on. Merges into an existing derived
   * candidate for the same pair, the way a machine `corroborate` would.
   */
  async assertRelation(
    entityId: string,
    input: { relationType: string; otherEntityId: string; thisIsSubject: boolean; validFrom?: string | null },
  ): Promise<{ relationId: string }> {
    return this.request("POST", `/api/entities/${entityId}/relations`, input);
  }

  // ---------------------------------------------------------------- people (UI-8)

  /** UI-8 — everyone the organization has heard, most-heard first. */
  async listPeople(): Promise<PersonCard[]> {
    return this.request<PersonCard[]>("GET", "/api/people");
  }

  /** WORLD-10 — everyone, every team, and the reporting and membership lines between them. */
  async getOrgChart(): Promise<OrgChart> {
    return this.request<OrgChart>("GET", "/api/people/org-chart");
  }

  /** UI-8 — one person's dossier: aliases, relations, insights and recordings. */
  async getPerson(id: string): Promise<PersonDossier> {
    return this.request<PersonDossier>("GET", `/api/people/${id}`);
  }

  // ---------------------------------------------------------------- world view (UI-10)

  /** UI-10 — every subject in this world view, most recently changed first. */
  async getWorldView(): Promise<WorldViewIndex> {
    return this.request<WorldViewIndex>("GET", "/api/world-view");
  }

  /** UI-23 — a pass in flight, the backlog waiting for the next one, and the last one
   * that finished. */
  async getWorldViewActivity(): Promise<WorldViewActivity> {
    return this.request<WorldViewActivity>("GET", "/api/world-view/activity");
  }

  // ---------------------------------------------------------------- insight processing (UI-24)

  /** UI-24 — recent synthesis batches, newest first, plus the backlog waiting for the next
   * one. `before` pages further back; omit for the first page. */
  async listSynthesisBatches(before?: string): Promise<SynthesisBatchList> {
    const params = new URLSearchParams();
    if (before) params.set("before", before);
    const query = params.toString();
    return this.request<SynthesisBatchList>("GET", `/api/world-view/batches${query ? `?${query}` : ""}`);
  }

  /** UI-24 — one batch in full: the funnel, its steps, every insight and verdict, what the
   * parser refused, the candidate pool it was shown, and what it derived downstream. */
  async getSynthesisBatch(id: string): Promise<SynthesisBatchDetail> {
    return this.request<SynthesisBatchDetail>("GET", `/api/world-view/batches/${id}`);
  }

  /** UI-24 — one step's own input and output artifacts, reached through its batch rather
   * than a source: a synthesis run has none. */
  async getBatchStepArtifacts(batchId: string, stepId: string): Promise<StepArtifact[]> {
    return this.request<StepArtifact[]>("GET", `/api/world-view/batches/${batchId}/steps/${stepId}/artifacts`);
  }

  /** UI-24 — one step's own model calls, in full. Owner/admin only; a 403 means asking an
   * admin, not that nothing was recorded. */
  async getBatchStepExchanges(batchId: string, stepId: string): Promise<ReasonerExchangeView[]> {
    return this.request<ReasonerExchangeView[]>("GET", `/api/world-view/batches/${batchId}/steps/${stepId}/exchanges`);
  }

  /**
   * UI-10 — one subject page, from the belief store. Not from the git working copy: the
   * repository is a publish target and the app never reads it back (WORLD-5).
   */
  async getSubject(slug: string): Promise<SubjectPageResponse> {
    return this.request<SubjectPageResponse>("GET", `/api/world-view/${encodeURIComponent(slug)}`);
  }

  /** UI-8's short role. Blank clears it. */
  async setPersonRole(id: string, role: string): Promise<void> {
    await this.request<null>("PUT", `/api/people/${id}/role`, { role });
  }

  /**
   * Ask for the dossier summary to be written. Returns as soon as the job is queued — the
   * paragraph arrives when a claimant takes the step, which is why the page polls rather
   * than waits. Asking again while one is in flight is a no-op, not an error.
   */
  async refreshPersonSummary(id: string): Promise<void> {
    await this.request<{ queued: boolean }>("POST", `/api/people/${id}/summary`);
  }

  /** UI-8 — dismiss a relation. A mark, not a delete, so `restoreRelation` brings back the
   * row that was removed rather than a different one. */
  async removeRelation(id: string): Promise<void> {
    await this.request<null>("DELETE", `/api/relations/${id}`);
  }

  async restoreRelation(id: string): Promise<void> {
    await this.request<null>("POST", `/api/relations/${id}/restore`);
  }

  /** UI-5 — who spoke and what was said, for one recording. */
  async getTranscript(id: string): Promise<Transcript> {
    return this.request<Transcript>("GET", `/api/recordings/${id}/transcript`);
  }

  // ---------------------------------------------------------------- review gate (INS-3, INS-4)

  /** INS-4 — every insight waiting on the owner's judgement. */
  async listReview(): Promise<ReviewInsight[]> {
    return this.request<ReviewInsight[]>("GET", "/api/review");
  }

  /**
   * The rail's Review-queue badge, without the whole list.
   *
   * `count` is both kinds together, because both are things that cannot be decided without a
   * human (INS-4 and AUTO-1) and a badge that counted one of them would send somebody to a
   * screen with more on it than the number promised.
   */
  async getReviewCount(): Promise<ReviewCounts> {
    return this.request<ReviewCounts>("GET", "/api/review/count");
  }

  /**
   * SPCH-8 — the proposals still waiting, across every recording (UI-16).
   *
   * A read, not a second way to answer them: the resolve call below is still the only
   * write, so a correction applied on a simple card is gone from Transcript Review and
   * the other way round.
   */
  async listCorrections(): Promise<PendingCorrection[]> {
    return this.request<PendingCorrection[]>("GET", "/api/corrections");
  }

  /** SPCH-6 — every voice still waiting on a name, across every recording (UI-16). */
  async listUnnamedSpeakers(): Promise<UnnamedSpeaker[]> {
    return this.request<UnnamedSpeaker[]>("GET", "/api/speakers/unnamed");
  }

  /** AUTO-1 — the contradictions waiting on the owner, drawn as cards in the review queue. */
  async listContradictions(): Promise<Contradiction[]> {
    return this.request<Contradiction[]>("GET", "/api/contradictions");
  }

  /**
   * AUTO-1 — acknowledge a contradiction, choose a side, or discard it.
   *
   * Choosing a side closes the losing belief's window and points it at the winner, which is
   * WORLD-1's supersede mechanic reused rather than a parallel path. The world view's files
   * catch up on the next synthesis batch.
   */
  async resolveContradiction(
    id: string,
    action: ContradictionAction,
    keepBeliefId?: string,
  ): Promise<void> {
    await this.request("POST", `/api/contradictions/${id}/resolve`, { action, keepBeliefId });
  }

  /**
   * UI-9 — the insights ledger, with every filter pushed into the Server's `WHERE` clause.
   *
   * One read answers the rows and every filter control's count, so the navigator panel and
   * the page cannot disagree about how many of anything there are.
   */
  async getLedger(query: LedgerQuery): Promise<LedgerView> {
    const params = new URLSearchParams({ horizon: query.horizon, state: query.state });
    if (query.verdict) params.set("verdict", query.verdict);
    if (query.source) params.set("source", query.source);
    if (query.text) params.set("q", query.text);

    return this.request<LedgerView>("GET", `/api/insights?${params}`);
  }

  /**
   * INS-4/INS-3 — approve an insight, which also accepts its entity tags and candidate
   * relations, and indexes it into search on the Server's own CPU (so approval does not
   * wait on the worker PC). Nothing leaves `pending` without this.
   *
   * `synthesizing` says this approval also started a world-view pass (WORLD-2). It is false
   * for most approvals in a review session and that is the debounce working, not a failure:
   * the first approval opens the batch and the rest land in the next one.
   */
  async approveInsight(id: string): Promise<{ id: string; embedded: boolean; synthesizing: boolean }> {
    return this.request<{ id: string; embedded: boolean; synthesizing: boolean }>(
      "POST",
      `/api/insights/${id}/approve`,
    );
  }

  /**
   * INS-7 — approve every pending insight of one source in one decision. `insightIds` is every
   * pending insight of the source the owner was shown; the Server refuses (409) when that is not
   * exactly what is waiting, so a list that was not fully loaded approves nothing.
   */
  async approveSource(sourceId: string, insightIds: string[]): Promise<SourceApproval> {
    return this.request<SourceApproval>("POST", `/api/sources/${sourceId}/insights/approve`, { insightIds });
  }

  /** INS-4 — discard an insight and remove it from the index. Kept as provenance, never indexed. */
  async discardInsight(id: string): Promise<void> {
    await this.request("POST", `/api/insights/${id}/discard`);
  }

  /** INS-4 — edit a pending insight's text before approving it. */
  async improveInsight(id: string, text: string): Promise<{ id: string; normalizedText: string }> {
    return this.request<{ id: string; normalizedText: string }>("PATCH", `/api/insights/${id}`, { text });
  }

  // ---------------------------------------------------------------- search (SRCH-1/2/3)

  /**
   * Hybrid search over both altitudes, narrowed by the scope and the four filters before
   * anything is ranked.
   *
   * POST rather than GET because a query is user content and will routinely be a whole
   * question, which has no business in a URL that lands in a log or a browser history.
   */
  async search(query: SearchQuery): Promise<SearchAnswer> {
    return this.request<SearchAnswer>("POST", "/api/search", query);
  }

  /**
   * SRCH-4 — find the words in transcripts, notes and documents. Its own route and its own
   * answer: a quote is never a search result, and it reaches what nobody has reviewed yet.
   */
  async searchTranscripts(query: TranscriptQuery): Promise<TranscriptSearchAnswer> {
    return this.request<TranscriptSearchAnswer>("POST", "/api/transcripts/search", query);
  }

  // ---------------------------------------------------------------- assistant (AUTO-4)

  /**
   * UI-15's Open loops tab: what the owner owes and what is owed to them (AUTO-4, M10.3).
   *
   * One read for both columns, because the counts above them and the rows below them should
   * not be able to disagree.
   */
  async getOpenLoops(): Promise<OpenLoop[]> {
    return this.request<OpenLoop[]>("GET", "/api/assistant/open-loops");
  }

  /**
   * Answer one loop: `done`, `dropped`, or `reopen`.
   *
   * Returns the refreshed list rather than nothing, so the screen redraws the counts that
   * just changed without asking twice.
   */
  async answerOpenLoop(id: string, action: OpenLoopAnswer): Promise<OpenLoop[]> {
    return this.request<OpenLoop[]>("POST", `/api/assistant/open-loops/${id}/answer`, { action });
  }

  /** UI-15's Suggestions tab: the periodic fuzzy pass's pending merge candidates (AUTO-2, M10.4). */
  async getEntitySuggestions(): Promise<EntitySuggestion[]> {
    return this.request<EntitySuggestion[]>("GET", "/api/assistant/entity-suggestions");
  }

  async acceptEntitySuggestion(
    id: string, input: { type: EntityType; name: string; role?: string | null },
  ): Promise<{ entityId: string; suggestions: EntitySuggestion[] }> {
    return this.request("POST", `/api/assistant/entity-suggestions/${id}/accept`, input);
  }

  /** UI-21 — the questions the system is asking, oldest first. Reading sweeps them first. */
  async listQuestions(): Promise<OwnerQuestion[]> {
    return this.request<OwnerQuestion[]>("GET", "/api/questions");
  }

  /** UI-21 — not worth asking. Never asked again. */
  async dismissQuestion(id: string): Promise<void> {
    await this.request<null>("POST", `/api/questions/${id}/dismiss`);
  }

  /** UI-21 — who owes an open loop the pass could not match anybody to. */
  async setCommitmentCounterparty(id: string, entityId: string): Promise<OpenLoop[]> {
    return this.request<OpenLoop[]>("PUT", `/api/assistant/open-loops/${id}/counterparty`, { entityId });
  }

  async rejectEntitySuggestion(id: string): Promise<EntitySuggestion[]> {
    return this.request<EntitySuggestion[]>("POST", `/api/assistant/entity-suggestions/${id}/reject`);
  }

  async getMergeSuggestions(): Promise<MergeSuggestion[]> {
    return this.request<MergeSuggestion[]>("GET", "/api/assistant/entity-merge-suggestions");
  }

  /**
   * Accept a suggestion: merge the other entity into `keepEntityId`. Returns the refreshed
   * queue, the same "redraw from what the server sent back" shape every answer here uses.
   */
  async acceptMergeSuggestion(id: string, keepEntityId: string): Promise<MergeSuggestion[]> {
    return this.request<MergeSuggestion[]>(
      "POST", `/api/assistant/entity-merge-suggestions/${id}/accept`, { keepEntityId });
  }

  /** Reject a suggestion: not the same entity. It will not be suggested again. */
  async rejectMergeSuggestion(id: string): Promise<MergeSuggestion[]> {
    return this.request<MergeSuggestion[]>(
      "POST", `/api/assistant/entity-merge-suggestions/${id}/reject`);
  }

  // ---------------------------------------------------------------- agents (SEC-5)

  /**
   * Approve an agent's request to read this account's knowledge (SEC-5, M9.1).
   *
   * This is where "a human user needs to login for an agent to be able to access data"
   * actually happens. The MCP server sends the browser here because it holds no Supabase
   * secret and cannot tell who is signed in; the Server answers with a short-lived assertion
   * saying this person approved this request, and the browser carries it back.
   *
   * The Server refuses to sign one for a redirect that is not the MCP server's own origin, so
   * this cannot be turned into an open redirect with a credential attached.
   */
  /**
   * The agents that currently hold live access to this account (SEC-7).
   *
   * Not under `/api/mcp`: that whole prefix is open to the MCP server's own service token by a
   * prefix match, and "it can read no knowledge at all" is the sentence that justifies that
   * credential existing.
   */
  async listAgents(): Promise<ConnectedAgent[]> {
    return this.request<ConnectedAgent[]>("GET", "/api/agents");
  }

  /**
   * What one agent has actually done, newest first (`[MCP-CORE-6]`, M9.6).
   *
   * Read from `mcp_calls`, and deliberately reachable only from here and never as an MCP tool:
   * the audit is a record kept *about* a credential, and a credential able to read its own audit
   * could find out which of its reaches were noticed.
   */
  async listAgentCalls(clientId: string, limit?: number): Promise<AgentCall[]> {
    const query = limit === undefined ? "" : `?limit=${limit}`;
    return this.request<AgentCall[]>(
      "GET", `/api/agents/${encodeURIComponent(clientId)}/calls${query}`);
  }

  /** Disconnect one agent. Answers the same whether or not it was connected. */
  async revokeAgent(clientId: string): Promise<void> {
    return this.request<void>("DELETE", `/api/agents/${encodeURIComponent(clientId)}`);
  }

  async approveAgent(request: AgentApproval): Promise<AgentApprovalResult> {
    return this.request<AgentApprovalResult>("POST", "/api/mcp/grants", request);
  }

  /** UI-5 — the recording detail's Insights and Entities tabs, in one payload. */
  async getRecordingInsights(id: string): Promise<RecordingInsights> {
    return this.request<RecordingInsights>("GET", `/api/recordings/${id}/insights`);
  }

  /** UI-7 — where an insight came from: the source spans to highlight in place. */
  async getInsightProvenance(id: string): Promise<InsightSpan[]> {
    return this.request<InsightSpan[]>("GET", `/api/insights/${id}/provenance`);
  }

  /**
   * SPCH-6 — confirm who a voice is. Pass a `personId` to link an existing person, or a
   * `name` to create one. Confirming is also what adds this recording's voice samples to
   * the gallery the next recording matches against.
   */
  async confirmSpeaker(
    id: string,
    speakerId: string,
    who: { personId: string } | { name: string },
  ): Promise<TranscriptSpeaker> {
    return this.request<TranscriptSpeaker>(
      "POST", `/api/recordings/${id}/speakers/${speakerId}/confirm`, who);
  }

  /**
   * SEC-4 — a fresh playback link, asked for each time a recording is opened.
   *
   * The Server answers with a path rather than a full address, because it does not
   * reliably know which hostname the browser reached it on. This is where that becomes
   * absolute, using the base the PWA was configured with — the same one every other
   * call goes to.
   */
  async createAudioLink(id: string): Promise<AudioLink> {
    const link = await this.request<AudioLink>("POST", `/api/recordings/${id}/audio-link`);

    return { ...link, url: `${this.baseUrl}${link.url}` };
  }

  /** UI-12 — one recording, word by word, for the review screen. */
  async getReview(id: string): Promise<TranscriptReview> {
    return this.request<TranscriptReview>("GET", `/api/recordings/${id}/review`);
  }

  /** Flag a segment for a second look, or take the flag back. */
  async flagSegment(id: string, segmentId: string, flagged: boolean): Promise<void> {
    await this.request("POST", `/api/recordings/${id}/segments/${segmentId}/flag`, { flagged });
  }

  /**
   * Move a segment to another voice. Null unassigns it, which is a real state on a
   * recording the diarizer found nobody in.
   *
   * The Server takes the segment's words with it; boundaries are left where reconcile
   * put them (DECISIONS.md 2026-08-28), so nothing the caller is holding disappears.
   */
  async reassignSegment(id: string, segmentId: string, speakerId: string | null): Promise<void> {
    await this.request("POST", `/api/recordings/${id}/segments/${segmentId}/speaker`, { speakerId });
  }

  /**
   * Retype a segment. Sending the pipeline's own text back reverts instead — a
   * correction that says nothing is not a correction.
   */
  async correctSegment(id: string, segmentId: string, text: string): Promise<{ correction: ReviewCorrection | null }> {
    return this.request<{ correction: ReviewCorrection | null }>(
      "POST", `/api/recordings/${id}/segments/${segmentId}/correction`, { text });
  }

  /** Take a correction back, and get the text it returns to. */
  async revertCorrection(id: string, segmentId: string): Promise<{ text: string }> {
    return this.request<{ text: string }>("DELETE", `/api/recordings/${id}/segments/${segmentId}/correction`);
  }

  /**
   * SPCH-8 — approve or discard one entity-aware correction the Reasoner proposed.
   * Approving makes it the segment's effective text; discarding keeps it as provenance.
   * A second thought costs the same as the first, so a decided proposal can be re-decided.
   */
  async resolveProposal(
    id: string,
    correctionId: string,
    apply: boolean,
  ): Promise<{ status: "applied" | "discarded" }> {
    return this.request<{ status: "applied" | "discarded" }>(
      "POST", `/api/recordings/${id}/corrections/${correctionId}/resolve`, { apply });
  }

  /** UI-12's *Mark reviewed*, and — sent false — the way back off it. */
  async setReviewed(id: string, reviewed: boolean): Promise<{ reviewedAt: string | null }> {
    return this.request<{ reviewedAt: string | null }>("POST", `/api/recordings/${id}/reviewed`, { reviewed });
  }

  /** UI-14 — the catalogue, what is chosen, and what the workers have on disk. */
  async getEngines(): Promise<EngineSettings> {
    return this.request<EngineSettings>("GET", "/api/engines");
  }

  /**
   * Place an engine, choose its model, set its parameters.
   *
   * Returns what was actually stored rather than what was asked for: leaving the model
   * out means "the default for this placement", and the screen should show the answer,
   * not the question.
   */
  async setEngine(
    engine: string,
    body: { placement: string; model?: string | null; parameters?: Record<string, unknown> },
  ): Promise<ResolvedEngine> {
    return this.request<ResolvedEngine>("PUT", `/api/engines/${encodeURIComponent(engine)}`, body);
  }

  /**
   * ARCH-12 — one step's parameters, under an engine already configured.
   *
   * Parameters only: there is no placement and no model to send, because neither is per
   * step. The whole engine comes back, not just the step, because a step row is drawn
   * inside its engine's card and the two are resolved together on the Server.
   */
  async setEngineStep(
    engine: string,
    step: string,
    parameters: Record<string, unknown>,
  ): Promise<ResolvedEngine> {
    return this.request<ResolvedEngine>(
      "PUT",
      `/api/engines/${encodeURIComponent(engine)}/steps/${encodeURIComponent(step)}`,
      { parameters },
    );
  }

  /** Back to what the step inherits — the engine's values over the catalogue's profile
   * for it. Not back to nothing: a step with no row of its own still has a temperament. */
  async resetEngineStep(engine: string, step: string): Promise<ResolvedEngine> {
    return this.request<ResolvedEngine>(
      "DELETE",
      `/api/engines/${encodeURIComponent(engine)}/steps/${encodeURIComponent(step)}`,
    );
  }

  async applyEnginePreset(preset: string): Promise<ResolvedEngine[]> {
    return this.request<ResolvedEngine[]>("POST", "/api/engines/preset", { preset });
  }

  async getProviders(): Promise<ProviderSettings> {
    return this.request<ProviderSettings>("GET", "/api/providers");
  }

  /**
   * Save and test, which is one action in the handoff and one here. The key goes up
   * and never comes back — what returns is presence, a hint and what the test found.
   */
  async saveProviderKey(provider: string, key: string): Promise<ProviderCredential> {
    return this.request<ProviderCredential>("PUT", `/api/providers/${encodeURIComponent(provider)}`, { key });
  }

  async removeProviderKey(provider: string): Promise<void> {
    await this.request<unknown>("DELETE", `/api/providers/${encodeURIComponent(provider)}`);
  }

  /**
   * UI-22 — everything the Costs section draws for one viewer-local month
   * (`YYYY-MM`), over ARCH-13's ledger. `tz` is an IANA zone name — the viewer's own,
   * so a call at 23:30 on the last day of the month lands in *their* month rather than
   * UTC's.
   */
  async getCostsActivity(month: string, tz: string): Promise<CostsActivity> {
    return this.request<CostsActivity>(
      "GET",
      `/api/costs?month=${encodeURIComponent(month)}&tz=${encodeURIComponent(tz)}`,
    );
  }

  /** SEC-3 — the machines allowed to claim work. */
  async listWorkers(): Promise<WorkerNode[]> {
    return this.request<WorkerNode[]>("GET", "/api/workers");
  }

  /**
   * Register a machine and get its token — the only time the token exists anywhere but
   * on the machine it is for. The Server keeps a hash, so a token that is not written
   * down is gone, and registering again is the only way back.
   */
  async registerWorker(name: string): Promise<IssuedWorker> {
    return this.request<IssuedWorker>("POST", "/api/workers", { name });
  }

  async revokeWorker(id: string): Promise<WorkerNode> {
    return this.request<WorkerNode>("POST", `/api/workers/${id}/revoke`);
  }

  /**
   * 0068 — pause a machine before a driver update or a reboot: it finishes the step it
   * holds and is handed nothing new until resumed.
   */
  async pauseWorker(id: string): Promise<WorkerNode> {
    return this.request<WorkerNode>("POST", `/api/workers/${id}/pause`);
  }

  async resumeWorker(id: string): Promise<WorkerNode> {
    return this.request<WorkerNode>("POST", `/api/workers/${id}/resume`);
  }

  /**
   * CAP-5 — the PCs allowed to file recordings.
   *
   * The same three calls as a worker's, against their own routes, because a capture PC
   * is the same kind of thing wearing a different job: a machine, in this organization,
   * holding a token that was shown once. What differs is entirely on the Server — one
   * kind claims steps and the other files recordings, and neither can do the other's
   * work with its own token.
   */
  async listCaptureDevices(): Promise<WorkerNode[]> {
    return this.request<WorkerNode[]>("GET", "/api/capture/devices");
  }

  async registerCaptureDevice(name: string): Promise<IssuedWorker> {
    return this.request<IssuedWorker>("POST", "/api/capture/devices", { name });
  }

  async revokeCaptureDevice(id: string): Promise<WorkerNode> {
    return this.request<WorkerNode>("POST", `/api/capture/devices/${id}/revoke`);
  }

  /**
   * CAP-2 — a file, sent to the Server a chunk at a time, which hashes the whole once
   * every chunk has landed and decides whether it has seen the bytes before (CAP-3).
   *
   * Chunked rather than one request: a single request holding a multi-gigabyte upload
   * open for minutes is exactly the shape of traffic some networks' NAT or firewall
   * equipment kills outright partway through (a fixed ~60 second cutoff, on one network
   * that upgraded the connection to HTTP/3). No chunk here is large enough to still be in flight a minute later, so
   * that failure has nothing to bite into — and a chunk that does fail to a plain
   * transient drop is retried on its own, without losing the rest of the file.
   *
   * `fetch` cannot report upload progress, and a four-gigabyte recording uploaded
   * behind a bar that never moves is indistinguishable from one that has stalled — so
   * each chunk still goes up over `XMLHttpRequest`, which can.
   */
  async upload(
    upload: NewUpload,
    { onProgress, signal }: { onProgress?: (fraction: number) => void; signal?: AbortSignal } = {},
  ): Promise<SourceMaterial> {
    const token = await this.accessToken();

    if (!token) throw new ApiError("Not signed in.", 401);
    if (signal?.aborted) throw new UploadCancelled();

    const created = await this.request<UploadSessionCreated>("POST", "/api/sources/uploads/sessions", {
      kind: upload.kind,
      filename: upload.file.name,
      sizeBytes: upload.file.size,
      title: upload.title,
      sourceDate: upload.sourceDate,
      language: upload.language,
      expectedSpeakers: upload.expectedSpeakers,
      authoredByMe: upload.authoredByMe === true ? true : undefined,
    });

    const base = `/api/sources/uploads/sessions/${created.sessionId}`;

    try {
      const total = upload.file.size;
      let offset = 0;

      while (offset < total) {
        const from = offset;
        const chunk = upload.file.slice(from, from + UploadChunkBytes);

        offset = await this.putChunkWithRetry(`${base}/chunks`, token, chunk, from, signal, (loaded) =>
          onProgress?.((from + loaded) / total),
        );
      }

      return await this.completeUploadSession(`${base}/complete`, token);
    } catch (cause) {
      // Best-effort: the sweeper reclaims an abandoned session on its own, this just
      // saves it the wait when the client already knows the upload is over.
      if (!(cause instanceof UploadCancelled)) this.cancelUploadSession(base, token);
      throw cause;
    }
  }

  /** One chunk, retried on a transient failure without spending the retry budget on
   * an offset mismatch — that is not a failure, it is a retry landing after the
   * Server had already accepted the bytes it thought it lost the answer to. */
  private async putChunkWithRetry(
    path: string,
    token: string,
    chunk: Blob,
    offset: number,
    signal: AbortSignal | undefined,
    onChunkProgress: (loaded: number) => void,
  ): Promise<number> {
    for (let attempt = 0; ; attempt++) {
      try {
        return await this.putChunk(path, token, chunk, offset, signal, onChunkProgress);
      } catch (cause) {
        if (cause instanceof UploadCancelled) throw cause;

        if (cause instanceof ChunkOffsetMismatch) return cause.bytesReceived;

        if (attempt >= UploadChunkRetries) throw cause;
        await sleep((attempt + 1) * 500);
      }
    }
  }

  private putChunk(
    path: string,
    token: string,
    chunk: Blob,
    offset: number,
    signal: AbortSignal | undefined,
    onChunkProgress: (loaded: number) => void,
  ): Promise<number> {
    return new Promise<number>((resolve, reject) => {
      if (signal?.aborted) {
        reject(new UploadCancelled());
        return;
      }

      const request = new XMLHttpRequest();
      request.open("PUT", `${this.baseUrl}${path}?offset=${offset}`);
      request.setRequestHeader("Authorization", `Bearer ${token}`);

      request.upload.onprogress = (event) => {
        if (event.lengthComputable) onChunkProgress(event.loaded);
      };

      request.onload = () => {
        const body = parseBody(request.responseText);
        const bytesReceived = body?.["bytesReceived"];

        if (request.status === 200 && typeof bytesReceived === "number") {
          resolve(bytesReceived);
          return;
        }

        if (request.status === 409 && typeof bytesReceived === "number") {
          reject(new ChunkOffsetMismatch(bytesReceived));
          return;
        }

        reject(new ApiError(describeProblem(body, request), request.status));
      };

      request.onerror = () => reject(new ApiError("The Server could not be reached.", 0));
      request.onabort = () => reject(new UploadCancelled());

      if (signal) signal.addEventListener("abort", () => request.abort(), { once: true });

      request.send(chunk);
    });
  }

  private completeUploadSession(path: string, token: string): Promise<SourceMaterial> {
    return new Promise<SourceMaterial>((resolve, reject) => {
      const request = new XMLHttpRequest();
      request.open("POST", `${this.baseUrl}${path}`);
      request.setRequestHeader("Authorization", `Bearer ${token}`);

      request.onload = () => {
        const body = parseBody(request.responseText);

        if (request.status === 201) {
          resolve(body as unknown as SourceMaterial);
          return;
        }

        if (request.status === 409 && body && "sourceMaterialId" in body) {
          reject(new DuplicateUploadError(body as unknown as DuplicateSource));
          return;
        }

        reject(new ApiError(describeProblem(body, request), request.status));
      };

      request.onerror = () => reject(new ApiError("The Server could not be reached.", 0));

      request.send();
    });
  }

  private cancelUploadSession(path: string, token: string): void {
    void fetch(`${this.baseUrl}${path}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } }).catch(
      () => {
        // Best-effort, as the doc comment on the caller says.
      },
    );
  }

  // ---------------------------------------------------------------- chat (CHAT-2)

  async getConversations(): Promise<ChatConversation[]> {
    return this.request<ChatConversation[]>("GET", "/api/chat/conversations");
  }

  async startConversation(): Promise<ChatConversation> {
    return this.request<ChatConversation>("POST", "/api/chat/conversations");
  }

  async deleteConversation(id: string): Promise<void> {
    await this.request<null>("DELETE", `/api/chat/conversations/${id}`);
  }

  async getConversation(id: string): Promise<ChatTurn[]> {
    return this.request<ChatTurn[]>("GET", `/api/chat/conversations/${id}/messages`);
  }

  /**
   * Ask a question and read the answer as it is written.
   *
   * Not `EventSource`, which is the obvious tool for this and cannot be used: it sends no
   * `Authorization` header, and every route here needs one. So this is `fetch` plus a
   * `ReadableStream`, and the frames are newline-delimited JSON — a format that survives a
   * chunk boundary landing in the middle of a line, which hand-rolled SSE does not.
   *
   * `onFrame` is called for every frame including the last, so a caller can append text as it
   * arrives and take the citations from `done` without a second request.
   */
  async ask(
    conversationId: string,
    question: string,
    onFrame: (frame: ChatFrame) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    const token = await this.accessToken();
    if (!token) throw new ApiError("Not signed in.", 401);

    const response = await fetch(
      `${this.baseUrl}/api/chat/conversations/${conversationId}/messages`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ question }),
        signal,
      },
    );

    if (!response.ok) throw new ApiError(await describeFailure(response), response.status);
    if (!response.body) throw new ApiError("The Server sent no answer.", 502);

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffered = "";

    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;

      buffered += decoder.decode(value, { stream: true });

      // Everything up to the last newline is whole frames; what follows it is the start of
      // one and waits for the next chunk.
      const lines = buffered.split("\n");
      buffered = lines.pop() ?? "";

      for (const line of lines) {
        if (line.trim().length === 0) continue;
        onFrame(JSON.parse(line) as ChatFrame);
      }
    }

    if (buffered.trim().length > 0) onFrame(JSON.parse(buffered) as ChatFrame);
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const token = await this.accessToken();

    if (!token) {
      throw new ApiError("Not signed in.", 401);
    }

    const response = await fetch(`${this.baseUrl}${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body === undefined ? {} : { "Content-Type": "application/json" }),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });

    if (!response.ok) {
      throw new ApiError(await describeFailure(response), response.status);
    }

    // 204 means the Server had a real answer and it was "nothing" — the pipeline route
    // uses it for a source with no run. Asking for JSON here would throw on an empty
    // body and turn that answer into a failure.
    if (response.status === 204) return null as T;

    return (await response.json()) as T;
  }
}

/** The user pressed cancel. Not something to apologise for on screen. */
export class UploadCancelled extends Error {
  constructor() {
    super("Upload cancelled.");
    this.name = "UploadCancelled";
  }
}

/** What `upload()` slices a file into. Comfortably under any plausible network's
 * stall window (see `upload()`'s own doc comment) and under Kestrel's default
 * request-body ceiling, so a chunk never needs a size override of its own. */
const UploadChunkBytes = 16 * 1024 * 1024;

/** How many times one chunk is retried after a transient failure before the whole
 * upload gives up. A dropped connection costs re-sending one chunk, not the file. */
const UploadChunkRetries = 3;

export interface UploadSessionCreated {
  sessionId: string;
  chunkSizeHint: number;
}

/** Not a failure: a chunk PUT landed after the Server had already accepted the
 * bytes it thought it lost the answer to. `bytesReceived` is where the file really
 * is, which is what lets the caller resync instead of giving up. */
class ChunkOffsetMismatch extends Error {
  constructor(readonly bytesReceived: number) {
    super("The Server already has more of this file than this chunk assumed.");
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function parseBody(text: string): Record<string, unknown> | null {
  try {
    return JSON.parse(text) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function describeProblem(body: Record<string, unknown> | null, request: XMLHttpRequest): string {
  const described = body?.["detail"] ?? body?.["title"];
  if (typeof described === "string" && described.length > 0) return described;

  return `${request.status} ${request.statusText}`.trim();
}

async function describeFailure(response: Response): Promise<string> {
  // Prefer the Server's ProblemDetails text; fall back to the status line when the
  // body is empty or not JSON, which is what a proxy error looks like.
  try {
    const problem = (await response.json()) as { title?: string; detail?: string };
    const described = problem.detail ?? problem.title;
    if (described) return described;
  } catch {
    // Fall through.
  }

  return `${response.status} ${response.statusText}`.trim();
}
